/**
 * NEN 6693 grondlaag-classificatie — de nieuwe (verplichte) systematiek.
 *
 * BRON = het officiële NEN 6693:2025-document (heilig). De NEN gebruikt **eigen** codes/
 * afkortingen die verschillen van de TerraIndex/SIKB-codes. De norm stelt (§4): *"De waarden
 * met omschrijving zijn leidend, de afkortingen zijn hulpmiddelen."* → wij bewaren intern de
 * **NEN-term + NEN-afkorting**; de `.ti`-export naar TerraIndex (dat NEN 6693 nog niet kent) is
 * een **NEN 6693 → NEN 5104/TI-vertaling** die ALLEEN bij export gebeurt (zie de `…ToTi`-maps).
 *
 * NEN-notatie is samengesteld (net als vroeger): grondsoort-hoofdletter + bijmenging-kleine-letter
 * + intensiteit-cijfer. Bv. `Zs1` = Zand zwak siltig, `Grk` = Grind kleiig. Kleur = 2-letter +
 * `li`/`do`-prefix (`dobr` = donker bruin). BZB = 3-letter (`GLT` = glauconiet).
 *
 * Codes hieronder komen uit: Tabel 9 (grind), 10 (organisch), 11 (klei), 12 (silt), 13 (zand),
 * 16 (kleuren), 35 (BZB-hoeveelheid), Bijlage C (BZB-soorten, Tabel C.1). TI-codes uit de
 * GeoVoice-Excel + echte TI-export. ⚠ = nog te verifiëren.
 *
 * Additief + namespaced (`nen6693.*`) naast de oude NEN 5104-lijsten; consumenten volgen later.
 */

// ─── 1. Grondsoort / laagtype (NEN-afkorting) ───────────────────────────────

/** Grondsoorten (term → NEN-afkorting, Tabel 9-13). Silt vervangt het afgeschafte 'leem'. */
export const GRONDSOORT = new Map<string, string>([
  // mineraal
  ['grind', 'Gr'],
  ['zand', 'Z'],
  ['silt', 'S'],
  ['klei', 'K'],
  // organisch (Tabel 10)
  ['veen', 'V'],
  ['humus', 'H'],
  ['detritus', 'DET'],
  ['gyttja', 'GY'],
  ['bruinkool', 'BK'], // organisch; geen eigen NEN-afkorting → intern 'BK'
]);

/** Grondsoort NEN-afkorting → TI `LaSoilTypeCode`. */
export const GRONDSOORT_TI: Record<string, string> = {
  Gr: 'G', Z: 'Z', S: 'J', K: 'K', V: 'V', H: 'H', DET: 'F', GY: 'Y',
  BK: 'V', // ⚠ bruinkool → veen (dichtstbijzijnde TI-grondsoort), verifiëren bij export
};

/** Organische grondsoort-afkortingen (voor conditionele vervolgvelden). */
export const GRONDSOORT_ORGANISCH = new Set(['V', 'H', 'DET', 'GY', 'BK']);

/**
 * Grondsoorten waarbij 'mineraalarm' opgegeven kan worden (organische lagen, excl. bruinkool).
 * Bij mineraalarm = ja krijgt de shortcode een `m` direct na de grondsoort: `Vms1` = veen
 * mineraalarm zwak siltig.
 */
export const MINERAALARM_GRONDSOORTEN = new Set(['V', 'H', 'DET', 'GY']);

/** Shortcode-letter voor mineraalarm (direct na de grondsoort). */
export const MINERAALARM_CODE = 'm';

/**
 * Speciale laagtypen (term → interne code). Andere vervolgvelden dan grondsoorten
 * (Slib: geen textuur/mediaan; Rots: verwijst naar een gesteentetype).
 */
export const LAAGTYPE_SPECIAAL = new Map<string, string>([
  ['slib', 'SLIB'],
  // 'W' en niet 'WATER': NEN 5104 gebruikt hier al 'W' (zie BODEMSOORT) en TerraIndex ook
  // (LaSoilType W = Water). Eén letter voor alle drie scheelt een vertaling én een chip die
  // een interne sleutel toont waar de gebruiker een norm-code verwacht.
  ['water', 'W'],
  ['rots', 'ROTS'], // sentinel → exporteren via GESTEENTE
  ['bodemvreemde laag', 'BVL'], // >50% bodemvreemd
  ['bijzondere laag', 'BIJZ'], // >50% bijzondere bestanddelen
]);

/** Speciale laagtypen → TI `LaSoilTypeCode`. ⚠ Bijzondere laag heeft geen echte TI-equiv. */
export const LAAGTYPE_SPECIAAL_TI: Record<string, string> = {
  SLIB: 'S', W: 'W', BVL: 'O', BIJZ: 'U', // ⚠ BIJZ → vullaag (of dominante grondsoort)
};

/**
 * Laagtypen waarvoor TerraIndex geen passende grond/steensoort heeft.
 *
 * De dichtstbijzijnde TI-waarden zijn 'Antropogene stort- of vullaag' (O) en 'Vullaag' (U). Voor
 * een luchtholte of een ballastbed is dat gewoon onwaar, en het veld is in TerraIndex niet
 * verplicht (nagelopen in de formulierdefinitie van de grondlaag). Draagt de laag al een bijzonder
 * bestanddeel, dan zegt dát wat de laag is en laten we de grondsoort bij export leeg -- liever
 * niets dan een verkeerde stelling.
 */
export const LAAGTYPE_ZONDER_TI_GRONDSOORT = new Set(['BVL', 'BIJZ']);

/** Codes zonder mediaan/textuur-bijmenging. */
export const LAAGTYPE_ZONDER_TEXTUUR = new Set(['SLIB', 'W', 'ROTS', 'BVL', 'BIJZ']);

/**
 * Mag een laag met deze grondsoort/dit laagtype textuur-bijmengingen dragen?
 *
 * Bij een bodemvreemde of bijzondere laag, slib, water of rots bestaat het veld niet: het
 * formulier toont die keuzes daar niet, want er ís geen grond om een textuur van te beschrijven.
 * Toch belandde "bodemvreemde laag, grindig sterk" in de database — onzichtbaar in het formulier,
 * wél zichtbaar in de laagregel en in de export. Wat de spreker daar noemt is een bijzonder
 * bestanddeel (grind), geen bijmenging.
 *
 * Een ONBEKENDE grondsoort levert `true` op, anders dan bij het boringtype. Een laag zonder
 * hoofdbestanddeel is een laag die nog wordt ingevuld; wie eerst de bijmenging typt en daarna de
 * grondsoort kiest, moet niet halverwege zijn invoer kwijtraken.
 */
export function acceptsAdmixtures(soilCode?: string | null): boolean {
  return !soilCode || !LAAGTYPE_ZONDER_TEXTUUR.has(soilCode);
}

/** Grondsoort-/laagtypecodes waarbij een 'consistentie'-veld getoond wordt (slib + klei/silt + organisch). */
export const CONSISTENTIE_GRONDSOORTEN = new Set(['SLIB', 'K', 'S', 'V', 'H', 'DET', 'GY', 'BK']);

/** Grondsoortcodes die de minerale-fijne-lagen-consistentie gebruiken (Tabel 24): klei en silt. */
export const MINERALE_CONSISTENTIE_GRONDSOORTEN = new Set(['K', 'S']);

/** Grondsoortcodes die de organische-lagen-consistentie gebruiken (Tabel 23): veen/humus/detritus/gyttja/bruinkool. */
export const ORGANISCHE_CONSISTENTIE_GRONDSOORTEN = new Set(['V', 'H', 'DET', 'GY', 'BK']);

/**
 * Consistentie van organische lagen — NEN 6693 Tabel 23. Zes klassen met omschrijving.
 * Opgeslagen waarde = de term (geen officiële NEN-afkorting).
 */
export const ORGANISCHE_CONSISTENTIE: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'zeer slap', description: 'Loopt bij knijpen zeer gemakkelijk tussen de vingers door.' },
  { term: 'slap', description: 'Loopt bij knijpen tussen de vingers door.' },
  { term: 'matig slap', description: 'Loopt bij knijpen nog goed tussen de vingers door.' },
  { term: 'matig stevig', description: 'Is met stevig knijpen nog juist tussen de vingers door te krijgen.' },
  { term: 'stevig', description: 'Is niet tussen de vingers door te krijgen.' },
  { term: 'vast', description: 'Is alleen nog met een nagel in te drukken.' },
];

/**
 * Consistentie van minerale fijne lagen (klei en silt) — NEN 6693 Tabel 24. Vijf klassen met
 * omschrijving. Opgeslagen waarde = de term (geen officiële NEN-afkorting).
 */
export const MINERALE_CONSISTENTIE: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'zeer slap', description: 'Een vinger kan gemakkelijk tot 25 mm in het monster worden gedrukt. De grond loopt tussen de vingers door wanneer een monster in de hand wordt samengeknepen.' },
  { term: 'slap', description: 'Een vinger kan tot 10 mm in het monster worden gedrukt. De grond kan met lichte druk uit de vingers worden gekneed.' },
  { term: 'stevig', description: 'Met de duim kan de grond gemakkelijk worden ingedrukt. De grond kan niet met de vingers worden gekneed, maar wel met de hand gerold tot 3 mm dikke strengen zonder te breken of te verkruimelen.' },
  { term: 'stijf', description: 'Met de duim kan een ondiepe indrukking worden gemaakt. De grond verkruimelt en breekt wanneer deze tot 3 mm dikke strengen wordt gerold, maar is nog vochtig genoeg om weer tot een bol te worden gekneed.' },
  { term: 'zeer stijf', description: 'Met de duimnagel kan een kerf in het monster worden gemaakt. De grond kan niet meer worden vervormd, maar verkruimelt onder druk. Veel uitgedroogde grond vertoont deze eigenschappen.' },
];

/**
 * Consistentie/rijping van slib, gegroepeerd voor de keuze-UI (niet-selecteerbare koppen).
 * Onder waterkolom = consistentie (Tabel 22); boven waterkolom = rijping (Tabel 25).
 * `hint` = wanneer je de groep gebruikt; `description` = de NEN-omschrijving per klasse.
 * Opgeslagen waarde = de term (er is geen officiële NEN-afkorting).
 */
export const SLIB_CONSISTENTIE_GROEPEN: ReadonlyArray<{
  label: string;
  hint: string;
  items: ReadonlyArray<{ term: string; description: string }>;
}> = [
  {
    label: 'Rijping (boven waterkolom)',
    hint: 'Gebruik deze rijpingsklassen voor slib dat bóven de waterkolom ligt (drooggevallen of rijpende waterbodem). Tabel 25.',
    items: [
      { term: 'ongerijpt', description: 'Zeer slap, loopt tussen de vingers door. (Rijpingsfactor > 2,0.)' },
      { term: 'vrijwel ongerijpt', description: 'Slap, kleeft sterk aan de handen en kan zeer gemakkelijk tussen de vingers door geperst worden. (Rijpingsfactor 1,4 tot 2,0.)' },
      { term: 'half gerijpt', description: 'Matig slap, kleeft aan de handen en kan gemakkelijk tussen de vingers door geperst worden. (Rijpingsfactor 1,0 tot 1,4.)' },
      { term: 'vrijwel gerijpt', description: 'Matig stijf, kleeft enigszins aan de handen en kan niet gemakkelijk tussen de vingers door geperst worden. (Rijpingsfactor 0,7 tot 1,0.)' },
      { term: 'gerijpt', description: 'Stijf, kleeft niet aan de handen en kan niet tussen de vingers door geperst worden. (Rijpingsfactor < 0,7.)' },
    ],
  },
  {
    label: 'Consistentie (onder waterkolom)',
    hint: 'Gebruik deze klassen voor slib dat zich ónder een waterkolom bevindt (de waterbodem). Tabel 22.',
    items: [
      { term: 'niet steekvast slib', description: 'Zeer slap, loopt tussen de vingers door. (Ongeconsolideerd slib.)' },
      { term: 'matig steekvast slib', description: 'Slap, kleeft sterk aan de handen en kan zeer gemakkelijk tussen de vingers door geperst worden. (Ongeconsolideerd slib.)' },
      { term: 'steekvast slib', description: 'Matig slap, kleeft aan de handen en kan gemakkelijk tussen de vingers door geperst worden. (Geconsolideerd slib.)' },
      { term: 'stevig/vast slib', description: 'Stevig, kleeft aan de handen en kan niet gemakkelijk tussen de vingers door geperst worden, heeft nog wel de kleur van slib. (Geconsolideerd slib.)' },
    ],
  },
];

/**
 * Gesteente-/rotstypen (Bijlage C.2, term → NEN-afkorting) — de officiële, canonieke lijst.
 * Dubbelrol: als hoofdbestanddeel = een rotslaag (kies 'rots' + hier het type); als brokken/
 * stukjes in een gewone laag = een BZB (zie BZB_GROEPEN). Overlap met C.1 (BRK/STK/GIP) is
 * bewust — zelfde code. Bruinkool bestaat hier (BRK) én als organische grondsoort (BK).
 */
export const GESTEENTE = new Map<string, string>([
  ['agglomeraat', 'AGG'], ['amfiboliet', 'AMF'], ['andesiet', 'AND'], ['anhydriet', 'ANH'],
  ['basalt', 'BST'], ['breccie', 'BRE'], ['bruinkool gesteente', 'BRK'], ['calcareniet', 'CAR'],
  ['calcilutiet', 'CIL'], ['calcirudiet', 'CIR'], ['calcisiltiet', 'CIS'], ['conglomeraat', 'CON'],
  ['dioriet', 'DIO'], ['doleriet', 'DOL'], ['dolomiet', 'DOM'], ['flint', 'FLI'],
  ['fylliet', 'FYL'], ['gabbro', 'GAB'], ['gips', 'GIP'], ['gneiss', 'GNE'],
  ['graniet', 'GRA'], ['granuliet', 'GUL'], ['haliet', 'HAL'], ['hoornrots', 'HOO'],
  ['ijzerzandsteen', 'IJZ'], ['kalksteen', 'KAS'], ['kleisteen', 'KST'], ['krijt', 'KRT'],
  ['kwartsiet', 'KWA'], ['leisteen', 'LEI'], ['marmer', 'MAR'], ['mergel', 'MER'],
  ['mignatiet', 'MIG'], ['moddersteen schalie', 'MOD'], ['myloniet', 'MYL'], ['pegmatiet', 'PEG'],
  ['peridotiet', 'PER'], ['pyroxeniet', 'PYR'], ['rhyoliet', 'RHY'], ['schist', 'SCT'],
  ['siltsteen', 'SIS'], ['steenkool', 'STK'], ['steenzout', 'STZ'], ['tektonische breccie', 'TBR'],
  ['tuf', 'TUF'], ['vast gesteente', 'VGS'], ['vulkanisch glas', 'VGL'], ['vulkanische breccie', 'VBR'],
  ['vuursteen', 'VST'], ['zandsteen', 'ZST'],
]);

/**
 * Gesteente NEN-afkorting → TI `LaSoilTypeCode` (voor een rotslaag). ⚠ Lang niet elk gesteente
 * heeft een TI-equivalent; onbekend → terugval bij export. Waarden uit de GeoVoice-Excel.
 */
export const GESTEENTE_TI: Record<string, string> = {
  BST: '44', GRA: '35', DIO: '38', AND: '40', GAB: '42', DOL: '43', GNE: '45',
  KAS: '27', KST: '25', KRT: '56', ZST: '23', CON: '22', BRE: '62', DOM: '55',
  GIP: '60', ANH: '59', HAL: '34',
  // Aanvullingen via exact-labelmatch tegen TI LaSoilType (rots als hoofdgrondsoort):
  CIL: '30', CIR: '28', CIS: '57', KWA: '49', LEI: '47', MER: '51', VST: '26',
  // TI kent deze wél, maar met een afwijkende/Engelse spelling (exact-match miste ze eerder):
  AGG: '31', SIS: '24', CAR: '29', TUF: '32', STK: '33', RHY: '37', SCT: '46', MAR: '48',
  HOO: '50', MOD: '53', FLI: '54', TBR: '62', VBR: '58', BRK: '63', STZ: '34', VGL: '41', IJZ: '23',
  // Geen exacte TI-code → naaste geologische verwant (best-effort, zodat de laag als gesteente exporteert):
  // amfiboliet/granuliet/migmatiet/myloniet → Gneis; fylliet → Leisteen; pegmatiet → Graniet;
  // peridotiet/pyroxeniet → Gabbro; vast gesteente → Stenen.
  AMF: '45', GUL: '45', MIG: '45', MYL: '45', FYL: '47', PEG: '35', PER: '42', PYR: '42', VGS: 'T',
};

/** Gesteente-codes waarvan de TI-grondsoortcode een naaste-verwant-benadering is (geen exacte TI-code). */
export const GESTEENTE_TI_APPROX = new Set(['AMF', 'GUL', 'MIG', 'MYL', 'FYL', 'PEG', 'PER', 'PYR', 'VGS']);

/**
 * Gesteente als BZB → TI `LaSc{n}TypeCode` (LaSpecialCompounds). Waar TerraIndex het gesteente
 * niet als BZB kent, valt bzbSoortToTi terug op 'BS' (bewerkt steen) — zie die functie.
 */
export const GESTEENTE_BZB_TI: Record<string, string> = {
  BST: 'B7', BRE: 'BC', CON: 'CG', DOL: 'DO', GIP: 'GI', IJZ: 'IZ', KAS: 'KS',
  KRT: 'KR', LEI: 'LS', MER: 'MG', SCT: '311', SIS: 'SE', VGS: 'VG', VST: 'VS', ZST: 'ZS',
};

/**
 * Groepering van hoofdbestanddelen voor de keuze-UI. De groep-labels zijn niet-selecteerbare
 * koppen (renderen als `<optgroup>`); alleen de items zijn kiesbaar. `term` = de opgeslagen
 * waarde (code via GRONDSOORT/LAAGTYPE_SPECIAAL), `label` = de weergavetekst.
 * NB: 'water' en 'bijzondere laag' staan bewust NIET in deze keuzelijst.
 */
export const HOOFDBESTANDDEEL_GROEPEN: ReadonlyArray<{
  label: string;
  items: ReadonlyArray<{ term: string; label: string }>;
}> = [
  {
    label: 'Minerale grondsoorten',
    items: [
      { term: 'zand', label: 'zand (Z)' },
      { term: 'klei', label: 'klei (K)' },
      { term: 'silt', label: 'silt (S)' },
      { term: 'grind', label: 'grind (Gr)' },
    ],
  },
  {
    label: 'Organische grondsoorten',
    items: [
      { term: 'veen', label: 'veen (V)' },
      { term: 'humus', label: 'humus (H)' },
      { term: 'detritus', label: 'detritus (DET)' },
      { term: 'gyttja', label: 'gyttja (GY)' },
      { term: 'bruinkool', label: 'Bruinkool organisch (BK)' },
    ],
  },
  {
    label: 'Bijzondere laagtypen',
    items: [
      { term: 'slib', label: 'slib' },
      // Water stond al in LAAGTYPE_SPECIAAL (WATER → TI 'W') en in LAAGTYPE_ZONDER_TEXTUUR,
      // maar niet in deze keuzelijst — en dit is de lijst die zowel de dropdown als de
      // AI-enum vult. Het was dus wel een geldig laagtype dat je nergens kon kiezen.
      { term: 'water', label: 'water' },
      // Label dekt beide gevallen: >50% bodemvreemd (puin, asfalt) én >50% bijzondere
      // bestanddelen zoals een luchtholte. De term blijft 'bodemvreemde laag' — dat is de
      // opgeslagen waarde en de AI-enum.
      { term: 'bodemvreemde laag', label: 'Bijzondere / Bodemvreemde laag' },
      { term: 'rots', label: 'rots / vast gesteente' },
    ],
  },
];

/**
 * Beslishulp 'Welke grondsoort kies ik?' — loop van boven naar beneden, de eerste die past is
 * de keuze (volgorde volgens NEN bijlage B). `tone` bepaalt de kleuraccent in de popup.
 */
export const HOOFDBESTANDDEEL_KEUZEHULP: ReadonlyArray<{
  title: string;
  description: string;
  tone: 'neutral' | 'blue' | 'green';
}> = [
  { title: 'Rots / vast gesteente', description: 'Vast gesteente dat je niet met de hand of boor losmaakt.', tone: 'neutral' },
  { title: 'Bijzondere / Bodemvreemde laag', description: 'Meer dan de helft is geen grond: door de mens gemaakt (puin, afval, asfalt) of een bijzonder bestanddeel zoals een luchtholte of ballast.', tone: 'neutral' },
  { title: 'Slib', description: 'De laag is slib.', tone: 'neutral' },
  { title: 'Grind (Gr)', description: 'Meer dan de helft is grind.', tone: 'blue' },
  { title: 'Organisch — veen, humus, detritus, gyttja, bruinkool', description: 'Veel organische stof: meer dan 15% in zand, of meer dan 30% in klei.', tone: 'green' },
  { title: 'Klei (K)', description: 'Plakt en lost niet makkelijk op in water.', tone: 'blue' },
  { title: 'Silt (S)', description: 'Plakt, maar lost wél makkelijk op in water.', tone: 'blue' },
  { title: 'Zand (Z)', description: 'Past niets hierboven? Dan is het zand.', tone: 'blue' },
];

// ─── 2. Bijmenging (textuur) + intensiteit-per-soort ────────────────────────

/**
 * Textuur-bijmengingen (term → NEN-afkorting). Grindig = `gr` (Tabel 9, "grind als bijmenging":
 * gr1/gr2/gr3); de overige zijn enkele letters (h/k/s/z). Composietvoorbeeld: `Zgr3` = zand
 * sterk grindig, `Zs2` = zand matig siltig, `Grz1` = grind zwak zandig.
 */
export const BIJMENGING = new Map<string, string>([
  ['grindig', 'gr'],
  ['humeus', 'h'],
  ['kleiig', 'k'],
  ['siltig', 's'],
  ['zandig', 'z'],
]);

/** Bijmenging NEN-afkorting → TI `LaAd{n}TypeCode`. */
export const BIJMENGING_TI: Record<string, string> = {
  gr: 'G', h: 'H', k: 'K', s: 'S', z: 'Z',
};

/**
 * Toegestane intensiteiten per bijmenging (NEN 6693). Sleutel = bijmenging NEN-afkorting.
 * `kleiig` (k) heeft géén intensiteit; alleen `siltig` (s) gaat door tot `uiterst`.
 */
export const BIJMENGING_INTENSITEIT: Record<string, readonly string[]> = {
  gr: ['zwak', 'matig', 'sterk'],
  h: ['zwak', 'matig', 'sterk'],
  k: [], // kleiig — geen gradatie
  s: ['zwak', 'matig', 'sterk', 'uiterst'],
  z: ['zwak', 'matig', 'sterk'],
};

/**
 * Organische hoofdbestanddelen volgen Tabel 10, niet Tabel 11 t/m 13. Die tabel kent maar zes
 * vormen: Vm (mineraalarm), Vk1, Vk3, Vz1, Vz3 — met H/DET/GY als gelijkwaardige varianten.
 * Daaruit volgen drie regels die voor minerale grondsoorten juist niet gelden:
 *
 *  1. Alleen kleiig en zandig. Siltig, grindig en humeus komen er niet in voor; 'veen humeus'
 *     is een dubbeling, want veen ís de organische stof.
 *  2. Kleiig krijgt hier WEL een klasse (Vk1/Vk3), terwijl kleiig bij een minerale grondsoort
 *     er juist géén heeft (Tabel 11 kent alleen 'k', Tabel 13 alleen 'Zk').
 *  3. Alleen zwak (1) en sterk (3). Matig en uiterst bestaan hier niet, en de klasse is
 *     verplicht — de tabel kent Vk1 en Vk3, geen kale Vk.
 *
 * En hooguit één bijmenging: bijlage D zet deze klassen in een driehoeksdiagram, waarin een
 * monster in precies één gebied valt. 'Kleiig én zandig' is daar geen bestaande uitkomst.
 *
 * BK (bruinkool) staat hier mee omdat de rest van het model dat als organisch behandelt, zie
 * CONSISTENTIE_GRONDSOORTEN. Tabel 10 noemt bruinkool niet.
 */
export const ORGANISCHE_GRONDSOORTEN: ReadonlySet<string> = new Set(['V', 'H', 'DET', 'GY', 'BK']);

/**
 * Tabel 10: de TEXTUUR-bijmengingen bij een organisch hoofdbestanddeel, met hun klassen.
 *
 * Grindig staat hier bewust niet tussen: dat is geen textuur maar een grindgehalte, en dat komt
 * uit een eigen diagram (fig. D.2) dat boven de rest ligt. Zie `admixtureRule` hieronder.
 */
export const ORGANISCH_BIJMENGING_INTENSITEIT: Record<string, readonly string[]> = {
  k: ['zwak', 'sterk'], // Vk1 / Vk3
  z: ['zwak', 'sterk'], // Vz1 / Vz3
};

/**
 * Hoeveel bijmengingen dit hoofdbestanddeel kan dragen.
 *
 * Organisch: twee. Eén textuur uit Tabel 10 (kleiig óf zandig -- in de driehoek van bijlage D
 * valt een monster in precies één gebied, dus 'kleiig én zandig' bestaat daar niet), plus
 * eventueel grindig.
 *
 * Dat laatste stond hier eerder niet: de grens van één ging over álle bijmengingen. Dat was een
 * te ruime lezing van dezelfde redenering. Grind komt niet uit de organische driehoek maar uit
 * fig. D.2, die er los boven ligt -- veen met grind erin bestaat gewoon, en 'veen, sterk zandig,
 * matig grindig' is een geldige beschrijving. De testtabel bij de korrelverdeling
 * (grondsoort-korrelverdeling.test.mjs) heeft er een geval voor.
 */
export function maxAdmixtures(soilCode?: string | null): number {
  return soilCode && ORGANISCHE_GRONDSOORTEN.has(soilCode) ? 2 : 3;
}

/**
 * De textuur-bijmengingen van een organische grondsoort (Tabel 10). Daarvan mag er één in een
 * laag staan: bijlage D zet ze in een driehoek en een monster valt in precies één gebied, dus
 * 'kleiig én zandig' bestaat daar niet. Grindig telt hier niet mee -- dat is geen textuur.
 */
export const ORGANISCH_TEXTUUR: ReadonlySet<string> = new Set(['k', 'z']);

/** Staat er al een textuur bij deze organische grondsoort? Dan kan er geen tweede bij. */
export function blocksSecondTexture(
  soilCode: string | null | undefined, admixture: string, used: readonly string[],
): boolean {
  if (!soilCode || !ORGANISCHE_GRONDSOORTEN.has(soilCode)) return false;
  if (!ORGANISCH_TEXTUUR.has(admixture)) return false;
  return used.some((a) => ORGANISCH_TEXTUUR.has(a));
}

export interface AdmixtureRule {
  /** Kan deze bijmenging überhaupt bij dit hoofdbestanddeel? */
  allowed: boolean;
  /** De klassen die de norm hier toestaat; leeg = geen gradatie. */
  intensities: readonly string[];
  /** Is een klasse verplicht? Alleen zo bij de organische vormen (Vk1, nooit Vk). */
  required: boolean;
}

/**
 * De enige plek waar 'welke klasse hoort bij deze bijmenging' wordt beslist. Zowel het
 * snelcode-toetsenbord als de keuzelijsten lezen hieruit, zodat ze niet uit elkaar kunnen lopen.
 */
export function admixtureRule(soilCode: string | null | undefined, admixture: string): AdmixtureRule {
  if (soilCode && ORGANISCHE_GRONDSOORTEN.has(soilCode)) {
    const intensities = ORGANISCH_BIJMENGING_INTENSITEIT[admixture];
    if (intensities) return { allowed: true, intensities, required: true };
    // Grindig hoort niet bij Tabel 10 maar bij de grinddriehoek (D.2), en die geldt ook voor
    // een organische laag. Klassen zoals overal: zwak/matig/sterk, en niet verplicht.
    if (admixture === 'gr') {
      return { allowed: true, intensities: BIJMENGING_INTENSITEIT.gr, required: false };
    }
    return { allowed: false, intensities: [], required: false };
  }
  return { allowed: true, intensities: BIJMENGING_INTENSITEIT[admixture] ?? [], required: false };
}

/** Intensiteit (term → code). NEN én TI gebruiken 1-4 (identiek). */
export const INTENSITEIT = new Map<string, string>([
  ['zwak', '1'], ['matig', '2'], ['sterk', '3'], ['uiterst', '4'],
]);

/**
 * De eigen bijmenging van een grondsoort — die mag niet als bijmenging gekozen worden
 * (je hebt geen 'zandig zand', 'kleiige klei', enz.). Sleutel = grondsoort-code, waarde =
 * uitgesloten bijmenging composiet-letter.
 */
export const GRONDSOORT_EIGEN_BIJMENGING: Record<string, string> = {
  Z: 'z',   // zand → zandig
  K: 'k',   // klei → kleiig
  S: 's',   // silt → siltig
  Gr: 'gr', // grind → grindig
  H: 'h',   // humus → humeus
};

// ─── 3. Mediaan (alleen zand en grind) ──────────────────────────────────────

/**
 * NEN 6693-mediaanklassen (term → interne code; die zijn gelijk). Alleen bij Zand en Grind.
 *
 * De korrelgroottetabel verdeelt zand onder fijn en middelgrof nog een keer; grind niet. De
 * samenvattende klassen blijven ernaast bestaan, en dat is met opzet: een laag die als 'fijn'
 * is vastgelegd zegt "tussen 63 en 200 µm", en dat is een eerlijke uitspraak. Die achteraf tot
 * een subklasse promoveren zou precisie verzinnen die niemand gemeten heeft. TerraIndex doet
 * het net zo -- zijn keuzelijst bij zand draagt dezelfde negen waarden.
 *
 * Gebruik `medianOptions(soilCode)` voor een keuzelijst; deze map is voor code ↔ term.
 */
export const MEDIAAN_KLASSE = new Map<string, string>([
  ['fijn', 'fijn'],
  ['fijn 63-105', 'fijn 63-105'],
  ['fijn 105-150', 'fijn 105-150'],
  ['fijn 150-200', 'fijn 150-200'],
  ['middelgrof', 'middelgrof'],
  ['middelgrof 200-300', 'middelgrof 200-300'],
  ['middelgrof 300-420', 'middelgrof 300-420'],
  ['middelgrof 420-630', 'middelgrof 420-630'],
  // Grof kent geen onderverdeling: de klasse zelf is 630 µm tot 2 mm.
  ['grof', 'grof'],
]);

/** Grondsoort-afkortingen waarvoor mediaan geldt (alleen zand en grind). */
export const MEDIAAN_GRONDSOORTEN = new Set(['Z', 'Gr']);

/**
 * Mediaanklassen met hun korrelgrootte per grondsoort — de korrelgroottetabel van NEN 6693.
 * Voedt het info-icoon bij het mediaan-veld én de keuzelijst (zie `medianOptions`).
 */
export const MEDIAAN_KORRELGROOTTE: Record<string, ReadonlyArray<{
  term: string;
  description: string;
  /** Een samenvattende klasse met subklassen eronder. Alleen bij zand; grind kent ze niet. */
  hoofdklasse?: true;
}>> = {
  // NEGEN keuzes bij zand: zeven gemeten fracties plus de twee samenvattende klassen.
  //
  // Die twee zijn er eerder uit gehaald met het argument dat 'fijn' niet vastlegt wat je
  // gemeten hebt. Dat klopt, maar het omgekeerde is erger: een veldwerker die alleen "zand
  // fijn" kan vaststellen werd gedwongen een fractie te kiezen die hij niet gemeten had, en
  // dan staat er precisie in de database die niemand kan verantwoorden. 'Fijn' is een eerlijke
  // uitspraak -- 63 tot 200 µm -- en TerraIndex draagt bij zand dezelfde negen waarden.
  //
  // De volgorde is die van de keuzelijst: eerst de hoofdklasse, dan zijn fracties. De UI
  // springt op `hoofdklasse` in om ze te laten inspringen.
  Z: [
    { term: 'fijn', description: '63 tot 200 µm', hoofdklasse: true },
    { term: 'fijn 63-105', description: '63 tot 105 µm' },
    { term: 'fijn 105-150', description: '105 tot 150 µm' },
    { term: 'fijn 150-200', description: '150 tot 200 µm' },
    { term: 'middelgrof', description: '200 tot 630 µm', hoofdklasse: true },
    { term: 'middelgrof 200-300', description: '200 tot 300 µm' },
    { term: 'middelgrof 300-420', description: '300 tot 420 µm' },
    { term: 'middelgrof 420-630', description: '420 tot 630 µm' },
    // Grof kent geen onderverdeling, dus is hij hier geen hoofdklasse maar gewoon een keuze.
    { term: 'grof', description: '630 µm tot 2,0 mm' },
  ],
  Gr: [
    // Bij grind zijn dit de klassen zélf, geen samenvatting -- dus geen hoofdklasse-vlag en
    // niets eronder.
    { term: 'fijn', description: '2,0 tot 6,3 mm' },
    { term: 'middelgrof', description: '6,3 tot 20 mm' },
    { term: 'grof', description: '20 tot 63 mm' },
  ],
};

/**
 * De mediaanklassen die bij deze grondsoort horen, in oplopende korrelgrootte.
 *
 * Soilbewust, want de onderverdeling bestaat alleen bij zand: een grindlaag een lijst met
 * 'fijn 105-150' voorschotelen zou onzin zijn -- dat is een zandmaat.
 */
export function medianOptions(soilCode: string | null | undefined): readonly string[] {
  return (MEDIAAN_KORRELGROOTTE[soilCode ?? ''] ?? []).map((m) => m.term);
}

/**
 * Alleen de klassen die een SHORTCODE-CIJFER dragen: de gemeten fracties, zonder de
 * samenvattende klassen.
 *
 * Dit moest los van `medianOptions`, en dat is het hele punt van deze functie. Het cijfer in
 * `Z5` is de plaats in deze lijst, en veldwerkers spreken die codes uit. Zouden de twee
 * hoofdklassen meetellen, dan verschoof `Z5` van 'middelgrof 300-420' naar 'middelgrof 200-300'
 * en betekende elke eerder ingesproken code iets anders -- een stille verschuiving door de hele
 * database heen. Bij grind verandert er niets: daar zijn 'fijn' en 'middelgrof' de klassen zelf.
 */
export function medianDigitOptions(soilCode: string | null | undefined): readonly string[] {
  return (MEDIAAN_KORRELGROOTTE[soilCode ?? ''] ?? []).filter((m) => !m.hoofdklasse).map((m) => m.term);
}

/** Is dit een samenvattende klasse met fracties eronder? (Alleen bij zand.) */
export function isMediaanHoofdklasse(soilCode: string | null | undefined, term: string | null | undefined): boolean {
  if (!term) return false;
  return !!(MEDIAAN_KORRELGROOTTE[soilCode ?? ''] ?? []).find((m) => m.term === term)?.hoofdklasse;
}

/**
 * De samenvattende zandklassen die uit de keuzelijst zijn gehaald, met de subklasse waar ze
 * in opgaan: de MIDDELSTE van de drie, want dat is de klasse waar 'fijn zand' zonder nadere
 * meting het vaakst op uitkomt.
 *
 * Alleen voor ZAND. Bij grind zijn 'fijn' en 'middelgrof' geen samenvatting maar de klassen
 * zelf (2,0-6,3 mm en 6,3-20 mm); die blijven onaangeroerd.
 */
export const MEDIAAN_VERVALLEN: Record<string, string> = {
  fijn: 'fijn 105-150',
  middelgrof: 'middelgrof 300-420',
};

/**
 * Een opgeslagen mediaan naar de huidige lijst brengen. Onbekende waarden blijven staan.
 *
 * DEZE OMZETTING IS ERUIT. Hij verving bij zand 'fijn' door 'fijn 105-150' en 'middelgrof' door
 * 'middelgrof 300-420', omdat die twee toen niet meer te kiezen waren. Nu ze weer in de lijst
 * staan zou dat betekenen dat je een hoofdklasse kiest en er stilletjes een fractie wordt
 * opgeslagen die je niet gemeten hebt -- precies de precisie-uit-het-niets waar het hele
 * onderscheid om draait.
 *
 * `MEDIAAN_VERVALLEN` blijft staan voor migratie 0025, die deze omzetting eenmalig op bestaande
 * rijen heeft uitgevoerd. Die rijen zijn niet terug te draaien: aan 'fijn 105-150' is niet meer
 * te zien of daar ooit 'fijn' stond of dat iemand die fractie zelf koos.
 */
export function normalizeMediaan(soilCode: string | null | undefined, term: string | null | undefined): string | null {
  return term || null;
}

/**
 * Mediaanklasse → cijfer voor het shortcode-invoerveld, en terug.
 *
 * NEN kent geen korte mediaan-afkorting (Bijlage E gebruikt woorden en µm), dus plaatsen we
 * een cijfer direct na de grondsoort: `Z5` = zand middelgrof 300-420, `Z5s2` = datzelfde zand
 * plus matig siltig.
 *
 * Het cijfer is de PLAATS in de lijst van die grondsoort, niet een vast nummer per term. Zand
 * heeft er zeven (1 = fijn 63-105 … 7 = grof), grind drie (1 fijn, 2 middelgrof, 3 grof). Dat
 * is de enige indeling die klopt zodra de lijsten per grondsoort verschillen, en hij blijft
 * kloppen als er ooit een klasse bij komt -- mits je dan ook deze regel leest.
 */
export function mediaanDigit(soilCode: string | null | undefined, term: string | null | undefined): string {
  if (!term) return '';
  // Een hoofdklasse heeft geen cijfer: hij staat niet in de genummerde reeks. Dat is geen
  // omissie maar het gevolg van wat hij betekent -- 'fijn' zegt juist dat de fractie niet
  // bekend is, en een cijfer zou er een verzinnen.
  const index = medianDigitOptions(soilCode).indexOf(term);
  return index < 0 ? '' : String(index + 1);
}

/** Het omgekeerde: cijfer → mediaanklasse van deze grondsoort, of null als het cijfer te hoog is. */
export function mediaanFromDigit(soilCode: string | null | undefined, digit: string | null | undefined): string | null {
  const index = Number(digit) - 1;
  const opties = medianDigitOptions(soilCode);
  return Number.isInteger(index) && index >= 0 && index < opties.length ? opties[index] : null;
}

/**
 * Mediaan → TI `LaMedianCode`, afhankelijk van de grondsoort.
 *
 * ZAND is geverifieerd (7 okt 2026) met een export uit TerraIndex zelf: een boring met alle
 * negen klassen, door TerraIndex weggeschreven. Daaruit komen de nummers hieronder. Tot dan
 * landden de zes fracties op de code van hun hoofdklasse ('fijn 63-105' werd 14 = Fijn 63-200):
 * een klasse die klopte maar grover was dan wat de veldwerker had vastgelegd.
 */
export const MEDIAAN_TI: Record<string, Record<string, string>> = {
  Z: {
    'fijn 63-105': '9', 'fijn 105-150': '8', 'fijn 150-200': '7', fijn: '14',
    'middelgrof 200-300': '12', 'middelgrof 300-420': '11', 'middelgrof 420-630': '10', middelgrof: '15',
    grof: '16',
  },
  // ⚠ GRIND is nog niet geverifieerd: de TerraIndex-testexport bevatte alleen zand.
  Gr: { fijn: '6', middelgrof: '5', grof: '4' },
};

// ─── 4. Kleur (Tabel 16) ────────────────────────────────────────────────────

/** De 15 NEN 6693-kleuren (term → NEN-afkorting, klein 2-letter). Hoofd- én bijkleur. */
export const KLEUR = new Map<string, string>([
  ['wit', 'wi'], ['grijs', 'gr'], ['zwart', 'zw'], ['rood', 'ro'], ['oranje', 'or'],
  ['geel', 'ge'], ['groen', 'gn'], ['blauw', 'bl'], ['paars', 'pa'], ['bruin', 'br'],
  ['roestbruin', 'rb'], ['beige', 'be'], ['crème', 'cr'], ['kleurloos', 'kl'], ['olijf', 'ol'],
]);

/** Kleur NEN-afkorting → TI `LaColourMainCode` / `LaColourSubCode`. (roestbruin rb → TI RS.) */
export const KLEUR_TI: Record<string, string> = {
  wi: 'WI', gr: 'GR', zw: 'ZW', ro: 'RO', or: 'OR', ge: 'GE', gn: 'GN', bl: 'BL',
  pa: 'PA', br: 'BR', rb: 'RS', be: 'BE', cr: 'CR', kl: 'KL', ol: 'OL',
};

/** Tint (term → NEN-prefix). NEN: `li`/`do`, standaard = geen prefix. */
export const KLEUR_TINT = new Map<string, string>([
  ['licht', 'li'],
  ['standaard', ''],
  ['donker', 'do'],
]);

/** Tint NEN-prefix → TI `LaColourShadeCode`. */
export const KLEUR_TINT_TI: Record<string, string> = {
  li: 'LI', '': 'NE', do: 'DR',
};

/** Kleuren (NEN-afkorting) waarbij géén licht/donker-tint mag (Tabel 16). */
export const KLEUR_TINT_UITGESLOTEN = new Set(['wi', 'zw', 'be', 'cr', 'kl']);

// ─── 5. Bijzondere bestanddelen (BZB) ───────────────────────────────────────

/**
 * BZB-soorten (term → NEN-afkorting, Tabel C.1 — de officiële "veelvoorkomende" lijst).
 * Niet in deze lijst = vrije tekst (NEN staat dat expliciet toe).
 */
export const BZB_SOORT = new Map<string, string>([
  ['aardewerk', 'AWK'], ['afval', 'AFV'], ['artefact', 'ART'],
  ['asbestverdacht materiaal', 'ASV'], ['asfalt', 'ASF'], ['baksteen', 'BAK'],
  ['bentoniet', 'BEN'], ['beton', 'BET'], ['bitumen', 'BIT'], ['bodemas', 'BAS'],
  ['bot', 'BOT'], ['bruinkool gesteente', 'BRK'], ['EPS (piepschuim)', 'EPS'], ['erts', 'ERT'],
  ['geotextiel', 'GTX'], ['gips', 'GIP'], ['glas', 'GLA'], ['glauconiet', 'GLT'],
  ['hout', 'HOU'], ['kalk', 'KAL'], ['keramiek', 'KER'], ['klei', 'KLE'],
  ['kolengruis', 'KOG'], ['lava', 'LAV'], ['leer', 'LEE'], ['lei(steen)', 'LEI'],
  ['menggranulaat', 'MGR'], ['metaal', 'MET'], ['(metaal)slak', 'MSL'],
  ['moddersteen', 'MOS'], ['plastic', 'PLA'], ['puin', 'PUI'], ['rioolslib', 'RSB'],
  ['rubber', 'RUB'], ['schelpen', 'SCH'], ['silt', 'SIL'], ['steenkool', 'STK'],
  ['stenen', 'STE'], ['veen', 'VEE'], ['vliegas', 'VLA'], ['vulkanische as', 'VAS'],
  ['zand', 'ZAN'], ['zinkas', 'ZAS'],
  // Wortels: speciaal BZB-type met eigen vervolgvelden (dichtheid + grootte, altijd
  // gebiedseigen). 'WO' is een interne app-code (geen officiële NEN/TI BZB-code). ⚠ export.
  ['wortels', 'WO'],
  // ─ Aanvulling uit de SIKB-codelijst 'BodemlaagBodemkenmerken' (geldige, niet-vervallen
  //   bijzondere bestanddelen die niet in NEN Tabel C.1 staan). De codes hieronder zijn
  //   INTERNE app-codes (geen officiële NEN/TI BZB-code) — TI-export ⚠. Grondsoorten,
  //   laagtypen, bodemkenmerken en duplicaten uit die lijst zijn bewust NIET opgenomen.
  // NB: brokjes/brokken (kleibrokjes/veenbrokjes/leembrokken) bewust NIET opgenomen — NEN 6693
  // hanteert dat concept expliciet niet ("inhomogeniteiten … wordt in NEN 6693 niet gehanteerd").
  ['kooldeeltjes', 'KOD'], ['slakken', 'SLK'],
  ['verf', 'VRF'], ['teer', 'TER'], ['olie', 'OLI'], ['sintels', 'SIN'],
  ['mijnsteen', 'MIJ'], ['asbest', 'ASB'], ['ballast', 'BAL'], ['basaltasfalt', 'BAF'],
  ['bewerkt steen', 'BWS'], ['brekerzand', 'BZA'], ['coproliet', 'COP'], ['fosfaat', 'FOS'],
  ['gestabiliseerd zand', 'GBZ'], ['goethietzand', 'GOZ'], ['gravel', 'GRV'],
  // Alleen AANGEBRACHT grind. NEN 6693 §3.2 zondert bij de definitie van een bijzonder
  // bestanddeel uitsluitend NATUURLIJK grind uit, en §3.9 rekent alleen "van nature in de
  // bodem voorkomende" schelpen en grind tot de grond. Aangebracht grind is dus geen grond
  // en hoort hier. Natuurlijk grind blijft de bijmenging 'grindig' (Tabel 9) of het
  // hoofdbestanddeel. Zie BZB_ALLEEN_ANTROPOGEEN: dat verschil zit in de HERKOMST, en
  // dezelfde laag mag grind nooit twee keer tellen -- één keer als textuur is één keer.
  ['grind', 'GRI'],
  ['houten vloer', 'HTV'],
  ['houtskool', 'HSK'], ['ijzer', 'IZR'], ['keien', 'KEI'], ['kleeflaag', 'KLF'], ['klinkers', 'KLK'],
  ['kolen', 'KOL'], ['koolas', 'KOA'], ['lavalith', 'LVL'], ['ligniet', 'LIG'], ['metselpuin', 'MSP'],
  ['oker', 'OKE'], ['riet', 'RIE'], ['split', 'SPL'], ['stelcon', 'STC'], ['stol', 'STO'],
  ['tegel', 'TEG'], ['textiel', 'TEX'], ['turf', 'TRF'], ['verbrand bot', 'VBT'],
  ['verbrande klei', 'VBK'], ['kunststofvezels', 'KSV'],
  ['vormzand', 'VMZ'], ['flugsand', 'FLU'], ['bims', 'BIM'], ['zandcement', 'ZCE'],
  ['porfier', 'POR'], ['planten', 'PLN'],
  // 'lucht' = luchtholte in de laag; staat niet in NEN 6693 Tabel C.1 maar wel in de
  // SIKB-codelijst (BZB_TYPE 'LU'). Op verzoek toch beschikbaar in de 6693-keuzelijst.
  ['lucht', 'LUC'],
]);

/** Term van het speciale BZB-type 'wortels' (eigen vervolgvelden, geen herkomst-keuze). */
export const BZB_WORTELS = 'wortels';

/** Term van het speciale BZB-type 'asfalt' (extra 'type asfalt'-keuze, altijd antropogeen). */
export const BZB_ASFALT = 'asfalt';

/** Term van het speciale BZB-type 'ballast' (extra fractie-keuze, altijd antropogeen). */
export const BZB_BALLAST = 'ballast';

/**
 * Ballastfractie (korrelmaat van het spoorballast) → TI `LaSc{n}TypeCode`. TerraIndex kent
 * geen los fractie-veld: elke fractie staat er als een eigen bijzonder bestanddeel
 * ('ballast fractie 31,5/50 mm'). In het formulier is het één keuze onder 'ballast' -- dat is
 * hoe de veldwerker het ziet -- en pas bij export valt het weer uiteen in die losse BZB's.
 * Zelfde constructie als de asfaltsoorten hierboven. Onbekende/lege fractie → 'BL' (ballast).
 */
export const BALLAST_FRACTIE_TI: Record<string, string> = {
  '31,5/50 mm': 'BF', '22/40 mm': 'BT', '30/63 mm': 'BD',
};

/**
 * Ballastfracties — extra vervolgkeuze bij het BZB-type 'ballast'. Opgeslagen = de term.
 * De volgorde is de keuzevolgorde in de dropdown: meest voorkomend bovenaan, niet oplopend
 * op korrelmaat.
 */
export const BALLAST_FRACTIES: readonly string[] = Object.keys(BALLAST_FRACTIE_TI);

/**
 * Asfalttypen — SIKB-codelijst 'BodemlaagBodemkenmerkenSpecificatie', groep 'Asfalt' (alleen
 * geldige, niet-vervallen). Extra vervolgkeuze bij het BZB-type 'asfalt'. Opgeslagen = de term.
 */
export const ASFALT_TYPEN: readonly string[] = [
  'combi deklaag 0/11', 'combi deklaag 0/16', 'combi deklaag 0/8', 'DAB', 'DAB 0/11', 'DAB 0/11-0/16',
  'DAB 0/16', 'DAB 0/6', 'DAB 0/6-0/8', 'DAB 0/8', 'DAB 0/8-0/11', 'dunne deklaag',
  'EAB', 'EAB 0/3', 'EAB 0/6', 'EAB 0/8', 'fijn asfalt', 'GAB',
  'GAB 0/16', 'GAB 0/16-0/32', 'GAB 0/32', 'gedesintegreerd asfalt', 'koud asfalt 0/11', 'koud asfalt 0/16',
  'Microville', 'Novachip 2/6', 'Novachip 4/8', 'Novachip 8/11', 'OAB', 'OAB 0/11',
  'OAB 0/11-0/16', 'OAB 0/16', 'OAB 0/16-0/22', 'OAB 0/22', 'OAB 0/8', 'OAB 0/8-0/11',
  'rode slijtlaag', 'slemlaag', 'slijtlaag', 'SMA', 'SMA 0/11', 'SMA 0/5',
  'SMA 0/6-0/8', 'SMA 0/8', 'SMA 0/8-0/11', 'STAB', 'STAB 0/16', 'STAB 0/16-0/22',
  'STAB 0/22', 'strijkasfalt', 'WAB', 'WAB 0/16', 'WAB 0/22', 'zandasfalt',
  'ZOAB', 'ZOAB 0/11', 'ZOAB 0/16', 'ZOAB 0/8', 'ZOAB fijn', '? 0/11',
  '? 0/16', '? 0/22', '? 0/6', '? 0/8', '? Asfalt', 'Gietasfalt',
  'AC 16 base', 'AC 22 base', 'AC 32 base', 'AC 16-22 base', 'AC 22-32 base', 'AC 11 bind',
  'AC 16 bind', 'AC 22 bind', 'AC 11-16 bind', 'AC 16-22 bind', 'AC 8 surf', 'AC 11 surf',
  'AC 16 surf', 'AC 8-11 surf', 'AC 11-16 surf', 'SMA-NL 5', 'SMA-NL 8', 'SMA-NL-11',
  'SMA-NL 5-8', 'SMA-NL 8-11', 'microdeklaag', 'GAB 0/8', 'OAB 0/16 type 1', 'OAB 0/16 type 2',
  'OAB 0/16 type 3', 'SMA 0/11 type 1', 'SMA 0/11 type 2', 'oppervlakbehandeling', 'brac', 'Stabiflex',
  'ZOAB 11', 'ZOAB 16', 'ZOAB 11-16', 'asfaltgranulaat', 'gestabiliseerd menggranulaat',
];

/**
 * Andere waarnemingen bij de olie-watertest — NEN 6693 Tabel 31. Optioneel extra veld dat naast
 * de olie-water-reactie ingevuld kan worden. Opgeslagen waarde = de term.
 */
export const OLIE_WATER_ANDERE_WAARNEMING: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'regenboogeffect kleuren', description: 'Regenboogeffect: benzine, terpentine, condensaat (aromaten).' },
  { term: 'blauw', description: 'Kleur blauw: diesel, ruwe olie.' },
  { term: 'groen', description: 'Kleur groen: teerolie, hydraulische olie.' },
  { term: 'dof', description: 'Dofheid: hoe doffer de drijflaag, hoe ouder de olie (ontbinding).' },
  { term: 'dampeffect', description: 'Dampeffecten: benzeen, bijvoorbeeld oudere benzine, ruwe olie, condensaat, verse benzine.' },
  { term: 'vorming bolletjes', description: 'Vorming bolletjes: boor- en snij-olie, vet.' },
  { term: 'schuimvormend', description: 'Schuimvormend: (af)wasmiddel.' },
  { term: 'zwarte drijvende deeltjes', description: 'Zwarte drijvende deeltjes: kolenstof.' },
  { term: 'vliezen op water', description: 'Vliezen van ijzerbacteriën.' },
];

/**
 * BZB NEN-afkorting → TI `LaSc{n}TypeCode`. Join op term (Tabel C.1 ↔ GeoVoice-Excel/oude
 * BZB_TYPE). ⚠ = geen schone TI-code (terugval + opmerking bij export).
 */
export const BZB_SOORT_TI: Record<string, string> = {
  AWK: 'AA', AFV: 'AF', ART: 'AR', ASV: 'AT', ASF: 'AS', BAK: 'BA',
  BEN: 'XX', // ⚠ bentoniet — geen TI-code
  BET: 'BE', BIT: 'BI',
  BAS: 'AK', // ⚠ bodemas — terugval koolas
  BOT: 'BO', BRK: 'BR', EPS: 'PM', ERT: 'ER',
  GTX: 'TX', // ⚠ geotextiel — terugval textiel
  GIP: 'GI', GLA: 'GS', GLT: 'GC', HOU: 'HO', KAL: 'KA', KER: 'KM', KLE: 'KL',
  KOG: 'KG', LAV: 'LL', LEE: 'LR', LEI: 'LS', MGR: 'M7', MET: 'ME', MSL: 'SL',
  MOS: 'MS', PLA: 'PC', PUI: 'PU',
  RSB: 'XX', // ⚠ rioolslib — geen schone TI-code
  RUB: 'RU', SCH: 'SC',
  SIL: 'XX', // ⚠ silt als BZB — ongebruikelijk
  STK: 'KO', STE: 'ST', VEE: 'VE', VLA: 'VL', VAS: 'VA', ZAN: 'ZA', ZAS: 'ZI',
  // SIKB-toegevoegde BZB's + wortels/planten: exact-labelmatch tegen de TI-codelijst
  // (LaSpecialCompounds). Zonder deze vielen ze allemaal weg bij export.
  WO: 'WO', PLN: 'PL', KOD: 'KD', SLK: 'SL', VRF: 'VF', TER: 'TE', OLI: 'OF', SIN: 'SI',
  MIJ: 'MI', ASB: 'AB', BAL: 'BL', BAF: 'T7', BWS: 'BS', BZA: 'BZ', COP: 'CO', FOS: 'FF',
  GBZ: 'SZ', GOZ: 'GO', GRV: 'GV', GRI: 'GR', HTV: 'HV', HSK: 'HK', IZR: 'YZ', KEI: 'KI', KLF: 'T8',
  KLK: 'KK', KOL: 'KO', KOA: 'AK', LVL: 'LL', LIG: 'LI', MSP: 'MP', OKE: 'OK', RIE: 'RI',
  SPL: 'SP', STC: 'SN', STO: 'SO', TEG: 'TG', TEX: 'TX', TRF: 'TU', VBT: 'VB', VBK: 'VK',
  KSV: 'KV', VMZ: 'VZ', FLU: 'FS', BIM: 'BM', ZCE: 'ZC', POR: 'PF',
  LUC: 'LU', // lucht → TI LaSpecialCompounds 'LU'
};

/**
 * Asfalt-subtype (label uit ASFALT_TYPEN) → TI `LaSc{n}TypeCode`. TerraIndex heeft álle
 * asfaltsoorten als losse BZB's in zijn 287-codelijst, dus we mappen elke subtype 1-op-1
 * i.p.v. platslaan naar generiek 'AS'. Alle 101 labels matchen exact met de TI-codelijst
 * (LaSpecialCompounds). Onbekende/lege subtype → terugval op 'AS' (generiek asfalt).
 */
export const ASFALT_TYPE_TI: Record<string, string> = {
  'combi deklaag 0/11': 'Z6', 'combi deklaag 0/16': 'Z7', 'combi deklaag 0/8': 'Z5',
  DAB: 'D0', 'DAB 0/11': 'D3', 'DAB 0/11-0/16': 'D7', 'DAB 0/16': 'D4', 'DAB 0/6': 'D1',
  'DAB 0/6-0/8': 'D5', 'DAB 0/8': 'D2', 'DAB 0/8-0/11': 'D6', 'dunne deklaag': 'T3',
  EAB: 'E0', 'EAB 0/3': 'E1', 'EAB 0/6': 'E2', 'EAB 0/8': 'E3', 'fijn asfalt': 'R2',
  GAB: 'G0', 'GAB 0/16': 'G1', 'GAB 0/16-0/32': 'G3', 'GAB 0/32': 'G2', 'gedesintegreerd asfalt': 'R3',
  'koud asfalt 0/11': 'K1', 'koud asfalt 0/16': 'K2', Microville: 'M1',
  'Novachip 2/6': 'N1', 'Novachip 4/8': 'N2', 'Novachip 8/11': 'N3',
  OAB: 'O0', 'OAB 0/11': 'O2', 'OAB 0/11-0/16': 'O6', 'OAB 0/16': 'O3', 'OAB 0/16-0/22': 'O7',
  'OAB 0/22': 'O4', 'OAB 0/8': 'O1', 'OAB 0/8-0/11': 'O5', 'rode slijtlaag': 'T2',
  slemlaag: 'T4', slijtlaag: 'T1', SMA: 'S0', 'SMA 0/11': 'S3', 'SMA 0/5': 'S1',
  'SMA 0/6-0/8': 'S4', 'SMA 0/8': 'S2', 'SMA 0/8-0/11': 'S5', STAB: 'X0', 'STAB 0/16': 'S7',
  'STAB 0/16-0/22': 'S9', 'STAB 0/22': 'S8', strijkasfalt: 'T5', WAB: 'W0', 'WAB 0/16': 'W1',
  'WAB 0/22': 'W2', zandasfalt: 'T6', ZOAB: 'Z0', 'ZOAB 0/11': 'Z2', 'ZOAB 0/16': 'Z3',
  'ZOAB 0/8': 'Z1', 'ZOAB fijn': 'Z4', '? 0/11': 'R6', '? 0/16': 'R7', '? 0/22': 'R8',
  '? 0/6': 'R4', '? 0/8': 'R5', '? Asfalt': 'R1', Gietasfalt: 'GA',
  'AC 16 base': 'A1', 'AC 22 base': 'A2', 'AC 32 base': 'A3', 'AC 16-22 base': 'A4', 'AC 22-32 base': 'A5',
  'AC 11 bind': 'A6', 'AC 16 bind': 'A7', 'AC 22 bind': 'A8', 'AC 11-16 bind': 'A9', 'AC 16-22 bind': 'C1',
  'AC 8 surf': 'C2', 'AC 11 surf': 'C3', 'AC 16 surf': 'C4', 'AC 8-11 surf': 'C5', 'AC 11-16 surf': 'C6',
  'SMA-NL 5': 'M2', 'SMA-NL 8': 'M3', 'SMA-NL-11': 'M4', 'SMA-NL 5-8': 'M5', 'SMA-NL 8-11': 'M6',
  microdeklaag: 'MD', 'GAB 0/8': 'G4', 'OAB 0/16 type 1': 'O8', 'OAB 0/16 type 2': 'O9',
  'OAB 0/16 type 3': 'B1', 'SMA 0/11 type 1': 'B2', 'SMA 0/11 type 2': 'B3', oppervlakbehandeling: 'B4',
  brac: 'B6', Stabiflex: 'SF', 'ZOAB 11': '1Z', 'ZOAB 16': '2Z', 'ZOAB 11-16': '3Z',
  asfaltgranulaat: '307', 'gestabiliseerd menggranulaat': 'SM',
};

/** Volumeaandeel (term → NEN-klasse, Tabel 35). Vervangt de oude losse intensiteit. */
export const BZB_VOLUME = new Map<string, string>([
  ['zeer weinig', 'zeer weinig'], // >0–2 %
  ['weinig', 'weinig'], // 2–5 %
  ['matig', 'matig'], // 5–15 %
  ['veel', 'veel'], // 15–50 %
  ['overvloedig', 'overvloedig'], // 50–80 %
  ['dominant', 'dominant'], // 80–100 %
]);

/** Volumeaandeel met omschrijving (%), voor het info-icoon bij BZB-intensiteit. Tabel 35. */
export const BZB_VOLUME_INFO: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'zeer weinig', description: '> 0 tot 2 %' },
  { term: 'weinig', description: '2 tot 5 %' },
  { term: 'matig', description: '5 tot 15 %' },
  { term: 'veel', description: '15 tot 50 %' },
  { term: 'overvloedig', description: '50 tot 80 %' },
  { term: 'dominant', description: '80 tot 100 %' },
];

/** Volumeaandeel NEN-klasse → TI `LaSc{n}GradationCode`. */
export const BZB_VOLUME_TI: Record<string, string> = {
  'zeer weinig': '17', weinig: '14', matig: '2', veel: '13', overvloedig: '19', dominant: '18',
};

/** Herkomst (term → interne code). */
export const BZB_HERKOMST = new Map<string, string>([
  ['antropogeen', 'antropogeen'], // bodemvreemd
  ['gebiedseigen', 'gebiedseigen'], // natuurlijk
]);

/** Herkomst → TI `LaSc{n}OriginCode`. ⚠ tag afgeleid, verifiëren. */
export const BZB_HERKOMST_TI: Record<string, string> = {
  antropogeen: 'AN', gebiedseigen: 'GE',
};

/**
 * Wortels — dichtheid (intensiteit). NEN 6693: dichtheid van wortels (wortels/dm²). Vervangt
 * de standaard volume-intensiteit bij het BZB-type 'wortels'. Opgeslagen waarde = de term.
 */
export const WORTELS_INTENSITEIT: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'geen wortels', description: '' },
  { term: 'zeer weinig', description: '1 tot 20 wortels/dm², of minder dan 4 over een lengte van 50 cm.' },
  { term: 'weinig', description: '20 tot 50 wortels/dm², of 4 tot 8 over een lengte van 50 cm.' },
  { term: 'gemiddeld', description: '50 tot 200 wortels/dm², of 8 tot 16 over een lengte van 50 cm.' },
  { term: 'veel', description: '> 200 wortels/dm², of meer dan 16 over een lengte van 50 cm.' },
];

/**
 * Wortels — grootte (diameter). NEN 6693 Tabel 28. Vervangt het herkomst-veld bij 'wortels'
 * (wortels zijn altijd gebiedseigen). Opgeslagen waarde = de term.
 */
export const WORTELS_GROOTTE: ReadonlyArray<{ term: string; description: string }> = [
  { term: 'zeer fijn', description: '< 0,5 mm' },
  { term: 'fijn', description: '> 0,5 mm en ≤ 2 mm' },
  { term: 'middel', description: '> 2 mm en ≤ 5 mm' },
  { term: 'grof', description: '> 5 mm' },
];

// ─── Compacte shortcode-codes voor snelinvoer (app-only, geen NEN-notatie) ────
// De NEN kent geen korte code voor volumeaandeel/herkomst; voor het invoerveld ('Code')
// gebruiken we een compacte weergave: soort (3-letter) + volume-cijfer + herkomst-suffix,
// bv. `GLT3AN` = glauconiet, matig, antropogeen.

/** BZB-volumeaandeel → cijfer voor het shortcode-invoerveld (1 = zeer weinig … 6 = dominant). */
export const BZB_VOLUME_SHORT: Record<string, string> = {
  'zeer weinig': '1', weinig: '2', matig: '3', veel: '4', overvloedig: '5', dominant: '6',
};

/**
 * Bestanddelen die alleen ANTROPOGEEN kunnen zijn.
 *
 * Grind is het geval waarvoor dit bestaat. NEN 6693 §3.2 zondert bij de definitie van een
 * bijzonder bestanddeel uitsluitend NATUURLIJK grind uit; aangebracht grind valt er dus wel
 * onder. §3.9 zegt het van de andere kant: alleen "van nature in de bodem voorkomende" schelpen
 * en grind horen bij de grond.
 *
 * Daaruit volgt één scheidslijn, en die loopt langs de herkomst:
 *
 *   natuurlijk grind   → textuur: bijmenging 'grindig' (Tabel 9), telt mee in de grindklasse
 *   aangebracht grind  → bijzonder bestanddeel, herkomst antropogeen, telt mee in het
 *                        totaal bodemvreemd (§9.2)
 *
 * Twee soorten grind in twee velden is juist. Hetzelfde grind in twee velden is dubbeltelling:
 * het zou zowel de grindklasse als het percentage bodemvreemd opdrijven.
 */
export const BZB_ALLEEN_ANTROPOGEEN: ReadonlySet<string> = new Set([
  'grind',
  // Asfalt stond al als "altijd antropogeen" in de code, maar alleen bij het WISSELEN van type;
  // de dropdown liet je 'm daarna gewoon op gebiedseigen zetten. Nu is het één regel.
  BZB_ASFALT,
  // Ballast is spoorwegballast: aangebracht steenslag. Van nature komt het niet voor, dus is
  // de herkomst geen waarneming maar een gevolg van de keuze -- het veld verdwijnt uit het
  // formulier en maakt plaats voor de fractie.
  BZB_BALLAST,
]);

/** BZB-herkomst → suffix voor het shortcode-invoerveld. */
export const BZB_HERKOMST_SHORT: Record<string, string> = {
  antropogeen: 'AN', gebiedseigen: 'GE',
};

/** BZB-keuzelijst gegroepeerd: bijzondere bestanddelen (C.1) + gesteenten (C.2, zonder C.1-dubbels). */
export const BZB_GROEPEN: ReadonlyArray<{ label: string; items: readonly string[] }> = [
  { label: 'Bijzondere bestanddelen', items: [...BZB_SOORT.keys()] },
  { label: 'Gesteenten', items: [...GESTEENTE.keys()].filter((t) => !BZB_SOORT.has(t)) },
];

/**
 * Elke term die als bijzonder bestanddeel gekozen mag worden — de platte versie van
 * BZB_GROEPEN.
 *
 * Dit is óók de toegestane verzameling voor het AI-extractieschema. Dat het één lijst is, is
 * het punt: stond de enum los, dan kon het model een term opleveren die de dropdown niet kent,
 * en dan staat er in het formulier een bestanddeel dat je niet kunt kiezen en niet kunt
 * corrigeren -- met een lege code, want er is niets om naar te vertalen.
 */
export const BZB_TERMEN: ReadonlyArray<string> = BZB_GROEPEN.flatMap((g) => g.items);

/**
 * Welke velden hoort een BZB-rij te tonen bij dít bestanddeel?
 *
 * Eén beschrijving voor alle drie de invoerkanten (backoffice, veldapp, AI-verwerking). Ze
 * liepen uit elkaar: in de veldapp kon je bij wortels een volumeaandeel kiezen dat daar niet
 * bestaat, en de grootte, het asfalttype en de ballastfractie waren er helemaal niet -- terwijl
 * de backoffice die velden wél toont en de herkomst juist verbergt waar hij vastligt. Zo
 * ontstonden er via de app combinaties die je met de hand niet kúnt maken.
 *
 * `herkomst` is niet-null als de keuze vastligt: dan verdwijnt het veld uit het formulier en
 * zet de opslag hem zelf. Zie normalizeBzbItem, die deze regels op de opgeslagen waarde toepast.
 */
export type BzbVeldregels = {
  /** Schaal van het hoeveelheidsveld: volumeaandeel (Tabel 35) of wortel-dichtheid. */
  intensiteit: 'volume' | 'wortels';
  /** Extra vervolgveld, of null. `key` is de sleutel op het bzb-item. */
  extra: null | {
    key: 'size' | 'asphaltType' | 'ballastFraction';
    label: string;
    opties: ReadonlyArray<{ term: string; description?: string }>;
  };
  /** Vaste herkomst (veld vervalt), of null als de gebruiker kiest. */
  herkomst: 'antropogeen' | 'gebiedseigen' | null;
};

const BZB_TERM_BY_CODE = new Map([...BZB_SOORT.entries()].map(([term, code]) => [code, term]));

/** Term bij een bzb-item: het item draagt de term, de code, of allebei. */
function bzbTerm(termOrCode: string | null | undefined): string {
  if (!termOrCode) return '';
  const v = String(termOrCode);
  return BZB_SOORT.has(v) ? v : (BZB_TERM_BY_CODE.get(v) ?? v);
}

export function bzbVeldregels(termOrCode: string | null | undefined): BzbVeldregels {
  const term = bzbTerm(termOrCode);
  if (term === BZB_WORTELS) {
    // Wortels tellen per dm², niet in volumeprocent, en zijn per definitie gebiedseigen.
    return {
      intensiteit: 'wortels',
      extra: { key: 'size', label: 'Grootte', opties: WORTELS_GROOTTE },
      herkomst: 'gebiedseigen',
    };
  }
  if (term === BZB_ASFALT) {
    return {
      intensiteit: 'volume',
      extra: { key: 'asphaltType', label: 'Type asfalt', opties: ASFALT_TYPEN.map((t) => ({ term: t })) },
      herkomst: 'antropogeen',
    };
  }
  if (term === BZB_BALLAST) {
    return {
      intensiteit: 'volume',
      extra: { key: 'ballastFraction', label: 'Fractie', opties: BALLAST_FRACTIES.map((t) => ({ term: t })) },
      herkomst: 'antropogeen',
    };
  }
  // Grind en andere bestanddelen die alleen aangebracht kunnen zijn: geen extra veld, wel een
  // herkomst die vastligt.
  return {
    intensiteit: 'volume',
    extra: null,
    herkomst: BZB_ALLEEN_ANTROPOGEEN.has(term) ? 'antropogeen' : null,
  };
}

/**
 * Een bzb-item zoals het opgeslagen MAG worden: velden die bij dit bestanddeel niet bestaan
 * gaan leeg, en een vastliggende herkomst wordt gezet. Zo kan geen enkel schrijfpad -- app,
 * backoffice of AI -- een combinatie opleveren die in het formulier niet te maken is.
 */
export function normalizeBzbItem<T extends Record<string, unknown>>(item: T): T {
  if (!item || typeof item !== 'object') return item;
  const r = bzbVeldregels((item.type as string) ?? (item.code as string));
  const houd = (key: 'size' | 'asphaltType' | 'ballastFraction') =>
    (r.extra?.key === key ? ((item[key] as string | null) ?? null) : null);
  return {
    ...item,
    size: houd('size'),
    asphaltType: houd('asphaltType'),
    ballastFraction: houd('ballastFraction'),
    origin: r.herkomst ?? ((item.origin as string | null) ?? null),
  };
}

/** Geldige consistentie-termen voor een grondsoort-/laagtypecode (leeg = veld niet tonen). */
export function consistentieTerms(soilCode: string | null | undefined): string[] {
  if (soilCode === 'SLIB') return SLIB_CONSISTENTIE_GROEPEN.flatMap((g) => g.items.map((i) => i.term));
  if (soilCode && MINERALE_CONSISTENTIE_GRONDSOORTEN.has(soilCode)) return MINERALE_CONSISTENTIE.map((i) => i.term);
  if (soilCode && ORGANISCHE_CONSISTENTIE_GRONDSOORTEN.has(soilCode)) return ORGANISCHE_CONSISTENTIE.map((i) => i.term);
  return [];
}

/**
 * Boorsysteem (boormethode) van een boring. Standaard-onafhankelijk: beschikbaar in zowel
 * NEN 5104 als NEN 6693. Bron: SIKB-codelijst 'Bemonsteringsapparaat', groep 'Bodembemonstering',
 * alléén geldige rijen (geen einddatum). `code` = SIKB-ID (opgeslagen waarde), `ti` = TI
 * `LaDrillingSystemTypeCode` (null waar TerraIndex de methode niet kent → terugval bij export).
 */
export const BOORSYSTEEM: ReadonlyArray<{ code: string; label: string; ti: string | null }> = [
  { code: '1', label: 'Edelmanboor', ti: 'ED' },
  { code: '2', label: 'Riversideboor', ti: 'RI' },
  { code: '7', label: 'Puinboor', ti: 'PU' },
  { code: '10', label: 'Ramguts', ti: 'RA' },
  { code: '12', label: 'Gutsboor', ti: 'GU' },
  { code: '13', label: 'Steektoestel zonder folie', ti: 'SZ' },
  { code: '14', label: 'Aqualock', ti: 'AQ' },
  { code: '15', label: 'Avegaarboor', ti: 'AV' },
  { code: '16', label: 'Beeker-sampler', ti: 'BS' },
  { code: '17', label: 'Begeman-sampler', ti: 'BG' },
  { code: '18', label: 'Box-corer', ti: 'BC' },
  { code: '19', label: 'Continuous soil sampler', ti: 'CO' },
  { code: '20', label: 'Drukkend boorsysteem', ti: 'DR' },
  { code: '21', label: 'Eckman-Birge happer', ti: 'EK' },
  { code: '22', label: 'Foliesteektoestel', ti: 'FO' },
  { code: '23', label: 'Grindboor', ti: 'GB' },
  { code: '24', label: 'Hamerend boorsysteem', ti: 'HB' },
  { code: '25', label: 'Handpuls', ti: 'PH' },
  { code: '26', label: 'Holle avegaar', ti: 'AH' },
  { code: '27', label: 'Jenkins-mudsampler', ti: 'JE' },
  { code: '28', label: 'Kernboor', ti: 'KB' },
  { code: '29', label: 'Mostap', ti: 'MO' },
  { code: '30', label: 'Multi-sampler', ti: 'MS' },
  { code: '31', label: 'Akkermanboor', ti: 'AK' },
  { code: '32', label: 'Pulsboor', ti: 'PS' },
  { code: '33', label: 'Sonisch boorsysteem', ti: 'SO' },
  { code: '34', label: 'Spitsmuisboor', ti: 'SM' },
  { code: '35', label: 'Valbom', ti: 'VB' },
  { code: '36', label: 'Van der Horst-steektoestel', ti: 'HO' },
  { code: '37', label: 'Van Veen happer', ti: 'VV' },
  { code: '38', label: 'Veenboor', ti: 'VN' },
  { code: '39', label: 'Vibro-corer', ti: 'VC' },
  { code: '40', label: 'Vrijwitboor', ti: 'VW' },
  { code: '41', label: 'Zuigerboor', ti: 'ZU' },
  // Waterbodem-apparaten zonder eigen TI-code → dichtste TI-tegenhanger (many-to-one).
  { code: '52', label: 'Bodemschaaf', ti: 'SC' },    // schraapt de toplaag van de waterbodem → Schep (onzeker)
  { code: '63', label: 'Modderpuls', ti: 'PS' },     // zelfde werkprincipe als puls, alleen ander medium
  { code: '66', label: 'Piston-corer', ti: 'ZU' },   // 'zuigerboor' is de NL naam voor een piston sampler
  { code: '68', label: 'Poliepgrijper', ti: 'GR' },  // grijper aan een kraan → machinaal ontgraven (onzeker)
  { code: '75', label: 'Slib sampler', ti: 'BS' },   // de Beeker is in NL dé slibsampler voor slappe waterbodem
  { code: '79', label: 'Spiraal', ti: 'SP' },
  { code: '80', label: 'Steekbuis', ti: 'ST' },
  { code: '91', label: 'Hamon happer', ti: 'VV' },   // zelfde familie als de Van Veen happer (onzeker)
  { code: '99', label: 'Graafmachine', ti: 'GR' },
  { code: '100', label: 'Schep', ti: 'SC' },
  { code: '101', label: 'Wortelboor', ti: 'WO' },
  { code: '102', label: 'Veenguts', ti: 'VE' },
  { code: '103', label: 'Geoprobe', ti: 'GP' },
  { code: '107', label: 'Stootijzer', ti: 'SY' },
  { code: '108', label: 'Vacuüm steekbuis', ti: 'ST' },
  // Geschrapt (geen monsternemer/laag): 50 Akoestische zandtransportmeter, 73 Sedimentval,
  // 77 Sondeerapparaat (= eigen meetpunttype; druk je een monsternemer de grond in → 20/103),
  // 109 Macrozoöbenthos zuiger.
];

const BOORSYSTEEM_BY_CODE = new Map(BOORSYSTEEM.map((b) => [b.code, b]));

/** Boorsysteem-code → label. */
export function boorsysteemLabel(code: string | null | undefined): string {
  return (code && BOORSYSTEEM_BY_CODE.get(code)?.label) || '';
}

/** Boorsysteem-code → TI `LaDrillingSystemTypeCode` (null als geen TI-equivalent, bv. casing). */
export function boorsysteemToTi(code: string | null | undefined): string | null {
  return (code && BOORSYSTEEM_BY_CODE.get(code)?.ti) ?? null;
}

/**
 * TerraIndex-code → BOORSYSTEEM-code. De omgekeerde weg van boorsysteemToTi.
 *
 * Het NEN 5104-extractiemodel levert het boorsysteem als TI-code ('ED'), terwijl de kolom
 * `drilling_system` in beide standaarden een BOORSYSTEEM-code bevat ('1') — daar rekenen de
 * export en de labels op. Zonder deze vertaling stond er een code die nergens een naam heeft.
 *
 * Meerdere boorsystemen kunnen dezelfde TI-code delen (TerraIndex kent minder methoden dan de
 * SIKB-lijst); de eerste uit de lijst wint, want die is de gangbare.
 */
const BOORSYSTEEM_BY_TI = new Map(
  BOORSYSTEEM.filter((b) => b.ti).reverse().map((b) => [b.ti as string, b.code]),
);
export function boorsysteemFromTi(ti: string | null | undefined): string | null {
  if (!ti) return null;
  return BOORSYSTEEM_BY_TI.get(ti) ?? null;
}

/**
 * Een boorsysteem-waarde uit een extractie omzetten naar de code die we opslaan.
 *
 * De twee lagenmodellen leveren het veld verschillend aan (NEN 6693 een BOORSYSTEEM-code, NEN
 * 5104 historisch een TerraIndex-code), en de kolom bevat er maar één soort. Onbekend blijft
 * staan zoals het kwam: liever een code zonder naam dan een leeg veld.
 */
export function normalizeBoorsysteem(value: string | null | undefined): string | null {
  if (!value) return null;
  if (BOORSYSTEEM_BY_CODE.has(value)) return value;
  return boorsysteemFromTi(value) ?? value;
}

/**
 * Boorsystemen zonder eigen TI-code, gemapt op de dichtste TI-tegenhanger — met de reden.
 * Wordt getoond in de codelijsten-tabel (definitie/opmerking) zodat de mapping traceerbaar is.
 */
export const BOORSYSTEEM_TI_NOTE: Readonly<Record<string, string>> = {
  '52': "Bodemschaaf schraapt de toplaag van de waterbodem → gemapt op 'Schep' (SC); geen exacte TI-tegenhanger (onzeker)",
  '63': "Modderpuls heeft hetzelfde werkprincipe als een puls, alleen een ander medium → gemapt op 'Pulsboor' (PS)",
  '66': "'Zuigerboor' (ZU) is de Nederlandse naam voor een piston sampler → vrijwel exacte match",
  '68': "Poliepgrijper = grijper aan een kraan → gemapt op 'Graafmachine' (GR), machinaal ontgraven (onzeker)",
  '75': "De Beeker-sampler (BS) is in Nederland dé slibsampler voor slappe waterbodem → dichtste match",
  '91': "Hamon happer zit in dezelfde familie als de 'Van Veen happer' (VV) — ander merk, zelfde werking (onzeker)",
};

/** Reden waarom een boorsysteem naar een alternatieve TI-code is gemapt (leeg = exacte/eigen code). */
export function boorsysteemTiNote(code: string | null | undefined): string {
  return (code && BOORSYSTEEM_TI_NOTE[code]) || '';
}

// ── Afwerking (boorgat dichten) — beide NEN-standaarden identiek ─────────────────
// Hangt aan het meetpunt. Drie blokken: vulling boorgat (VUL_), casing, bovenafwerking (BOV_).
// Codes zijn per domein geprefixed zodat ze niet botsen (bv. BK).

/** Blok 1 — vulmateriaal per traject (van/tot). Verplicht: minstens één regel. */
export const VULMATERIAAL: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'VUL_FG', label: 'Filtergrind' },
  { code: 'VUL_FZ', label: 'Filterzand' },
  { code: 'VUL_AZ', label: 'Aanvulzand' },
  { code: 'VUL_AG', label: 'Aanvulgrind' },
  { code: 'VUL_BK', label: 'Bentoniet (korrels of pellets)' },
  { code: 'VUL_BP', label: 'Bentoniet (poeder)' },
  { code: 'VUL_BS', label: 'Bentonietsuspensie of grout' },
  { code: 'VUL_CE', label: 'Cement' },
  { code: 'VUL_KL', label: 'Klei' },
  { code: 'VUL_UG', label: 'Uitkomende grond' },
  { code: 'VUL_OP', label: 'Open, niet aangevuld' },
];

/** Blok 3 — bovenafwerking (alleen bij een peilbuis). Optioneel; staat niet in NEN 6693 (SIKB/BRO).
 *  Elke optie is een concreet TerraIndex-product mét het materiaal al ingebakken (TI codeert het
 *  materiaal ín de FiTypeCode) → géén losse materiaalkeuze meer nodig, en de TI-export is exact
 *  1-op-1 (zie BOVENAFWERKING_TI). Curatie op de NL-peilbuispraktijk; niche-TI-codes vallen onder
 *  'Anders'. Oude codes (generieke BOV_SP/BOV_PD/BOV_BK) blijven geldig voor bestaande data. */
export const BOVENAFWERKING: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'BOV_SP_GI',  label: 'Straatpot gietijzer' },
  { code: 'BOV_SP_RVS', label: 'Straatpot RVS' },
  { code: 'BOV_SP_KS',  label: 'Straatpot kunststof' },
  { code: 'BOV_SP_AL',  label: 'Straatpot aluminium' },
  { code: 'BOV_SP',     label: 'Straatpot (overig/onbekend)' },
  { code: 'BOV_VP',     label: 'Vloerpot RVS 3 mm' },
  { code: 'BOV_VP4',    label: 'Vloerpot RVS 4 mm' },
  { code: 'BOV_PD_GI',  label: 'Putdeksel gietijzer' },
  { code: 'BOV_PD_ME',  label: 'Putdeksel metaal' },
  { code: 'BOV_PD_CO',  label: 'Putdeksel composiet' },
  { code: 'BOV_PD',     label: 'Putdeksel (overig)' },
  { code: 'BOV_BK_ST',  label: 'Beschermkoker staal' },
  { code: 'BOV_BK_KS',  label: 'Beschermkoker kunststof' },
  { code: 'BOV_BB_PVC', label: 'Beschermbuis PVC ø110 mm' },
  { code: 'BOV_AK',     label: 'Afdekkap metaal' },
  { code: 'BOV_GE',     label: 'Geen afwerking' },
  { code: 'BOV_AN',     label: 'Anders' },
];

/** @deprecated Materiaal zit nu ín het bovenafwerkingstype (zie BOVENAFWERKING). Alleen nog
 *  aanwezig voor achterwaartse compatibiliteit met bestaande data/velden. */
export const BOVENAFWERKING_MATERIAAL: ReadonlyArray<string> = [
  'Gietijzer', 'Staal', 'RVS', 'Kunststof', 'Aluminium', 'Beton', 'Composiet',
];

/** Labels van uitgefaseerde bovenafwerking-codes, zodat bestaande data leesbaar blijft. */
const BOVENAFWERKING_LEGACY_LABELS: Readonly<Record<string, string>> = {
  BOV_BK: 'Beschermkoker (bovengronds)',
};

/** Spraaksynoniemen voor vulmateriaal (voor de AI-parser): term → VUL_-code. */
export const VULMATERIAAL_SYNONYMS: Readonly<Record<string, string>> = {
  zwelklei: 'VUL_BK', // veldwerker zegt 'zwelklei', bedoelt bentoniet
};

/** Vulmateriaal → TerraIndex FiTypeCode (afwerking-codelijst 28). Geverifieerd tegen
 *  TI_afwerking.json. 'Open, niet aangevuld' kent geen TI-vulmateriaal → 'AN' (anders)
 *  met toelichting in FiRemark. */
export const VULMATERIAAL_TI: Readonly<Record<string, string>> = {
  VUL_FG: 'FG', // filtergrind
  VUL_FZ: 'FZ', // filterzand
  VUL_AZ: 'AZ', // aanvulzand
  VUL_AG: 'AG', // aanvulgrind
  VUL_BK: 'BE', // bentoniet (korrels/pellets) → bentoniet
  VUL_BP: 'BO', // bentoniet (poeder) → 'Bentoniet stof'
  VUL_BS: 'GR', // bentonietsuspensie of grout → grout
  VUL_CE: 'CE', // cement
  VUL_KL: 'KL', // klei
  VUL_UG: 'UG', // uitkomende grond
  VUL_OP: 'AN', // open, niet aangevuld → 'anders' (geen TI-vulmateriaal)
};

/**
 * Blok 2 — "achtergelaten in de grond" (codelijst C, GeoVoice-eigen; NEN Bijlage C-stijl:
 * kort, met vrije tekst 'OVE' als vangnet). `aard` is het kern-onderscheid:
 *  - 'geplaatst'    = bewust geplaatst, hoort hier (bv. diver/sensor/bron)
 *  - 'achtergebleven' = onbedoeld achtergebleven, hier zit een probleem (bv. vastgelopen casing)
 *  - 'beide'        = kan allebei; de gebruiker kiest zelf de aard
 * De peilbuis zit hier bewust NIET in (die heeft een eigen object met filterstelling).
 */
export type AchtergelatenAard = 'geplaatst' | 'achtergebleven' | 'beide';
export const ACHTERGELATEN_TYPE: ReadonlyArray<{ code: string; label: string; aard: AchtergelatenAard }> = [
  { code: 'CAS', label: 'Casing of verbuizing', aard: 'achtergebleven' },
  { code: 'FIL', label: 'Filter of buisdeel',   aard: 'achtergebleven' },
  { code: 'GER', label: 'Boorgereedschap',      aard: 'achtergebleven' },
  { code: 'DIV', label: 'Diver of datalogger',  aard: 'geplaatst' },
  { code: 'SEN', label: 'Sensor',               aard: 'geplaatst' },
  { code: 'BRO', label: 'Bron',                 aard: 'geplaatst' },
  { code: 'OVE', label: 'Anders, zie opmerking', aard: 'beide' },
];

/** Codelijst C → TerraIndex FiTypeCode. Alles gaat naar één Fi-record; onbekende → 'AN'
 *  met aard/materiaal samengevat in FiRemark (TI kent die velden niet). */
export const ACHTERGELATEN_TYPE_TI: Readonly<Record<string, string>> = {
  CAS: 'CA', DIV: 'DI', SEN: 'WA', FIL: 'MF', GER: 'AN', BRO: 'AN', OVE: 'AN',
};

/** Bovenafwerking → TerraIndex FiTypeCode (afwerking-codelijst 28). TerraIndex gooit vulling,
 *  bovenafwerking en casing op één hoop in dezelfde FiTypeCode-lijst — een vloerpot/straatpot
 *  is dus gewoon een Fi-record. Onze BOVENAFWERKING-codes zijn zó gekozen dat elk 1-op-1 op een
 *  TI-code mapt (materiaal zit al in de code) → geen gok meer. Geverifieerd tegen TI_afwerking.json. */
export const BOVENAFWERKING_TI: Readonly<Record<string, string>> = {
  // Straatpot (materiaal-specifiek in TI).
  BOV_SP_GI: 'SG', // straatpot gietijzer
  BOV_SP_RVS: '51', // straatpot roestvaststaal
  BOV_SP_KS: '50', // straatpot plastic
  BOV_SP_AL: 'SM', // straatpot aluminium
  BOV_SP: 'SN', // straatpot niet afsluitbaar (generiek/overig)
  // Vloerpot (TI enkel RVS: 3mm/4mm).
  BOV_VP: 'V3', // vloerpot 3mm RVS
  BOV_VP4: 'V4', // vloerpot 4mm RVS
  // Putdeksel.
  BOV_PD_GI: 'GP', // gietijzeren putdeksel
  BOV_PD_ME: 'MP', // metalen putdeksel
  BOV_PD_CO: 'CP', // composiet putdeksel
  BOV_PD: 'PD', // putdeksel (generiek/overig)
  // Bovengrondse bescherming.
  BOV_BK_ST: 'BS', // beschermkoker staal
  BOV_BK_KS: 'BP', // beschermkoker plastic
  BOV_BB_PVC: '58', // beschermbuis PVC ø110mm, 300mm
  BOV_BK: 'BS', // legacy generieke beschermkoker → staal
  // Afdekkap / geen / anders.
  BOV_AK: 'AM', // afdekkap metaal boven de grond
  BOV_GE: 'GE', // geen afwerking
  BOV_AN: 'AN', // anders
};
/** Legacy: oude generieke codes (BOV_SP/BOV_PD/BOV_BK) sloegen het materiaal apart op. Om bestaande
 *  data zuiver te blijven exporteren mappen we die combinatie alsnog naar de materiaal-specifieke
 *  TI-code. Nieuwe data stuurt geen materiaal meer mee (zit in de code) → deze tabel raakt 'm niet. */
const BOVENAFWERKING_TI_LEGACY_MATERIAL: Readonly<Record<string, Record<string, string>>> = {
  BOV_SP: { gietijzer: 'SG', aluminium: 'SM', rvs: '51', kunststof: '50', staal: '51' },
  BOV_PD: { gietijzer: 'GP', staal: 'MP', composiet: 'CP' },
  BOV_BK: { kunststof: 'BP', composiet: 'BP', staal: 'BS' },
};

const VULMATERIAAL_BY_CODE = new Map(VULMATERIAAL.map((v) => [v.code, v]));
const BOVENAFWERKING_BY_CODE = new Map(BOVENAFWERKING.map((b) => [b.code, b]));
const ACHTERGELATEN_BY_CODE = new Map(ACHTERGELATEN_TYPE.map((t) => [t.code, t]));

export function vulmateriaalLabel(code: string | null | undefined): string {
  return (code && VULMATERIAAL_BY_CODE.get(code)?.label) || '';
}
export function vulmateriaalTi(code: string | null | undefined): string | null {
  return (code && VULMATERIAAL_TI[code]) || null;
}
export function bovenafwerkingLabel(code: string | null | undefined): string {
  return (code && (BOVENAFWERKING_BY_CODE.get(code)?.label || BOVENAFWERKING_LEGACY_LABELS[code])) || '';
}
export function achtergelatenTypeLabel(code: string | null | undefined): string {
  return (code && ACHTERGELATEN_BY_CODE.get(code)?.label) || '';
}
/** Achtergelaten object → TerraIndex FiTypeCode (casing→CA, diver→DI, …; onbekend→AN). */
export function achtergelatenTi(code: string | null | undefined): string | null {
  return (code && ACHTERGELATEN_TYPE_TI[code]) || null;
}
/** Bovenafwerking → TerraIndex FiTypeCode. Nieuwe codes mappen 1-op-1 (materiaal zit al in de
 *  code). `material` is alleen nog een legacy-vangnet: oude generieke codes (BOV_SP/BOV_PD/BOV_BK)
 *  met een apart materiaal → de materiaal-specifieke TI-code. */
export function bovenafwerkingTi(code: string | null | undefined, material?: string | null): string | null {
  if (!code) return null;
  const m = (material || '').toLowerCase().trim();
  const byMat = BOVENAFWERKING_TI_LEGACY_MATERIAL[code];
  if (byMat && m && byMat[m]) return byMat[m];
  return BOVENAFWERKING_TI[code] || null;
}
/** Zet een opgeslagen bovenafwerking (type + los materiaal) om naar de huidige samengestelde code,
 *  zodat oude data in de nieuwe (materiaal-vrije) keuzelijst de juiste optie toont. Codes die al in
 *  de huidige lijst staan blijven ongewijzigd; alleen de oude generieke codes met een materiaal
 *  worden opgewaardeerd. */
export function bovenafwerkingMigrate(type: string | null | undefined, material?: string | null): string | null {
  if (!type) return null;
  if (BOVENAFWERKING_BY_CODE.has(type)) return type; // al een geldige (nieuwe) code
  const m = (material || '').toLowerCase().trim();
  const map: Record<string, Record<string, string>> = {
    BOV_SP: { gietijzer: 'BOV_SP_GI', rvs: 'BOV_SP_RVS', kunststof: 'BOV_SP_KS', aluminium: 'BOV_SP_AL', staal: 'BOV_SP_RVS' },
    BOV_PD: { gietijzer: 'BOV_PD_GI', staal: 'BOV_PD_ME', composiet: 'BOV_PD_CO' },
    BOV_BK: { kunststof: 'BOV_BK_KS', composiet: 'BOV_BK_KS', staal: 'BOV_BK_ST' },
  };
  if (map[type] && m && map[type][m]) return map[type][m];
  // Generiek zonder (herkenbaar) materiaal → de 'overig'-variant die nog in de lijst staat.
  if (type === 'BOV_BK') return 'BOV_BK_ST';
  return type; // onbekend → laat staan (dropdown toont dan niets, export via legacy-pad blijft werken)
}
/** Standaard-aard bij een type ('beide' → null, gebruiker kiest zelf). */
export function achtergelatenDefaultAard(code: string | null | undefined): 'geplaatst' | 'achtergebleven' | null {
  const a = code ? ACHTERGELATEN_BY_CODE.get(code)?.aard : null;
  return a === 'geplaatst' || a === 'achtergebleven' ? a : null;
}

/**
 * Reken een sjabloon-afwerkingsprofiel (percentages, van BOVEN naar BENEDEN) om naar concrete
 * vultrajecten (van/tot in cm -maaiveld) op basis van de boordiepte. Het laatste traject sluit
 * exact af op de boordiepte (afrondingsrest daarheen). Percentages worden op hun som genormaliseerd.
 */
export function afwerkingFillingsFromProfile(
  profile: ReadonlyArray<{ percentage?: number | null; van?: number | null; tot?: number | null; materiaal?: string | null }> | null | undefined,
  boordiepteCm: number,
): Array<{ van: number; tot: number; materiaal: string | null; opmerking: null }> {
  const all = profile || [];
  if (!all.length || !boordiepteCm || boordiepteCm <= 0) return [];
  const out: Array<{ van: number; tot: number; materiaal: string | null; opmerking: null }> = [];

  // Nieuw formaat: elk traject heeft van%/tot% (0–100, moeten aansluiten + samen 100% dekken).
  // Traject in cm = percentage × boordiepte / 100.
  const isRange = all.some((r) => r?.tot != null || r?.van != null);
  if (isRange) {
    for (const r of all) {
      if (!r?.materiaal || r.van == null || r.tot == null) continue;
      const van = Math.round((Number(r.van) / 100) * boordiepteCm);
      let tot = Math.round((Number(r.tot) / 100) * boordiepteCm);
      if (tot > boordiepteCm) tot = boordiepteCm;
      if (tot <= van) continue;
      out.push({ van, tot, materiaal: r.materiaal || null, opmerking: null });
    }
    return out;
  }

  // Legacy formaat: losse percentages van boven naar beneden (cumulatief, op hun som genormaliseerd).
  const rows = all.filter((r) => (Number(r?.percentage) || 0) > 0 && r?.materiaal);
  if (!rows.length) return [];
  const total = rows.reduce((s, r) => s + (Number(r.percentage) || 0), 0);
  if (total <= 0) return [];
  let van = 0;
  rows.forEach((r, i) => {
    const pct = Number(r.percentage) || 0;
    let tot = i === rows.length - 1 ? boordiepteCm : Math.round(van + (pct / total) * boordiepteCm);
    if (tot > boordiepteCm) tot = boordiepteCm;
    if (tot <= van) return;
    out.push({ van, tot, materiaal: r.materiaal || null, opmerking: null });
    van = tot;
  });
  if (out.length) out[out.length - 1].tot = boordiepteCm;
  return out;
}

/**
 * Maaiveldtype van een boring. Standaard-onafhankelijk (beide NEN-standaarden). Bron: SIKB-codelijst
 * 'MaaiveldType' (geldige rijen). `code` = SIKB-ID (opgeslagen waarde), `ti` = TI `MpGroundLevelTypeCode`.
 */
export const MAAIVELDTYPE: ReadonlyArray<{ code: string; label: string; ti: string | null }> = [
  { code: '1', label: 'asfaltbeton', ti: 'AB' },
  { code: '3', label: 'asfalt', ti: 'AS' },
  { code: '4', label: 'baksteen', ti: 'BA' },
  { code: '5', label: 'beton', ti: 'BE' },
  // 'berm' en 'braak' staan NIET in de SIKB-codelijst maar wél in TerraIndex, en de veldwerker
  // komt ze tegen. Ze hebben dus geen SIKB-ID; hun code is de TI-code zelf. Dat is te zien aan
  // de letters: een SIKB-rij heeft een nummer. Zolang de SIKB-export het maaiveldtype niet
  // meeneemt (nu zo) heeft dat geen gevolgen; gaat dat ooit veranderen, dan moeten deze twee
  // daar worden overgeslagen of alsnog een SIKB-ID krijgen.
  { code: 'BM', label: 'berm', ti: 'BM' },
  { code: 'BR', label: 'braak', ti: 'BR' },
  { code: '12', label: 'grind', ti: 'GR' },
  { code: '13', label: 'gras', ti: 'GS' },
  { code: '14', label: 'gravel', ti: 'GV' },
  { code: '15', label: 'houten vloer', ti: 'HV' },
  { code: '16', label: 'kassei', ti: 'KA' },
  { code: '17', label: 'kiezel', ti: 'KI' },
  { code: '18', label: 'klinker', ti: 'KL' },
  { code: '21', label: 'puin', ti: 'PU' },
  { code: '22', label: 'stelcon', ti: 'SC' },
  { code: '23', label: 'strooisel', ti: 'SL' },
  { code: '24', label: 'stol', ti: 'SO' },
  { code: '25', label: 'split', ti: 'SP' },
  { code: '26', label: 'steen', ti: 'ST' },
  { code: '27', label: 'silex', ti: 'SX' },
  { code: '28', label: 'teer', ti: 'TE' },
  { code: '29', label: 'tegel', ti: 'TL' },
  { code: '31', label: 'verharding', ti: 'VE' },
  { code: '32', label: 'waterbodem', ti: 'WA' },
];

const MAAIVELDTYPE_BY_CODE = new Map(MAAIVELDTYPE.map((m) => [m.code, m]));

/** Maaiveldtype-code → label. */
export function maaiveldtypeLabel(code: string | null | undefined): string {
  return (code && MAAIVELDTYPE_BY_CODE.get(code)?.label) || '';
}

/** Maaiveldtype-code → TI `MpGroundLevelTypeCode`. */
export function maaiveldtypeToTi(code: string | null | undefined): string | null {
  return (code && MAAIVELDTYPE_BY_CODE.get(code)?.ti) ?? null;
}

/**
 * Werksoort van een project — het doel/type van het (water)bodemonderzoek. Bepaalt (later) welke
 * beschrijvingsaspecten en administratieve velden verplicht zijn. `nenScope` = het NEN 6693-
 * toepassingsgebied (Bijlage A) dat de regelset stuurt; `description` voedt het info-icoon.
 * Voor nu is dit puur een keuzeveld met toelichting; de regelengine volgt later.
 */
export const WERKSOORT: ReadonlyArray<{ code: string; label: string; nenScope: string; short: string; description: string }> = [
  {
    code: 'verkennend',
    label: 'Verkennend bodemonderzoek',
    nenScope: 'Verkennend (water)bodemonderzoek',
    short: 'Eerste milieuhygiënisch onderzoek; volledige laagbeschrijving verplicht.',
    description:
      'Eerste, verkennend onderzoek naar de milieuhygiënische bodemkwaliteit. NEN 6693-regelset: "verkennend (water)bodemonderzoek" — de volledige laagbeschrijving (hoofdstuk 8) is verplicht.',
  },
  {
    code: 'nader',
    label: 'Nader bodemonderzoek',
    nenScope: 'Nader (water)bodemonderzoek',
    short: 'Verontreiniging afperken; consistentie van organische lagen verplicht.',
    description:
      'Vervolgonderzoek om een aangetroffen verontreiniging af te perken. NEN 6693-regelset: "nader (water)bodemonderzoek" — o.a. de consistentie van organische lagen wordt verplicht.',
  },
  {
    code: 'asbest',
    label: 'Asbest in bodem',
    nenScope: 'Asbest in (water)bodemonderzoek',
    short: 'Asbest in bodem; extra eisen (boordiameter ≥ 12 cm, vochtigheidsklasse).',
    description:
      'Onderzoek naar asbest in de (water)bodem. NEN 6693-regelset: "asbest in (water)bodemonderzoek" + SIKB-protocol 2018 — extra eisen zoals boordiameter ≥ 12 cm, geïnspecteerd oppervlak, zeef-maaswijdte en grove fractie; vochtigheidsklasse wordt verplicht.',
  },
  {
    code: 'waterbodem',
    label: 'Waterbodemonderzoek',
    nenScope: 'Waterbodem',
    short: 'Waterbodem; nulpunt = waterspiegel, "water boven maaiveld" verplicht.',
    description:
      'Onderzoek van een waterbodem (sloot, plas, rivier). NEN 6693-regelset: waterbodem — het nulpunt verschuift: maaiveldtype wordt meestal de waterspiegel en het referentievlak de bovenkant van de waterbodem/het waterpeil. "Water boven maaiveld" is verplicht.',
  },
  {
    code: 'grondwater',
    label: 'Grondwateronderzoek / -monitoring',
    nenScope: 'Grondwateronderzoek',
    short: 'Grondwater via peilbuizen; laagbeschrijving vervalt, grondwaterbeschrijving i.p.v.',
    description:
      'Onderzoek of monitoring van grondwater via peilbuizen. NEN 6693-regelset: "grondwateronderzoek" — de laagbeschrijving (hoofdstuk 8) vervalt; in plaats daarvan geldt hoofdstuk 11 (grondwater: kleur, helderheid, geur, bijzondere bestanddelen).',
  },
  {
    code: 'partijkeuring',
    label: 'Partijkeuring grond',
    nenScope: 'Buiten NEN 6693',
    short: 'Keuring partij grond; buiten NEN 6693, wel NEN-vocabulaire.',
    description:
      'Keuring van een partij grond. Valt búiten NEN 6693 (geen toepassingsgebied/verplichtingenset), maar je gebruikt wél het NEN-vocabulaire (grondsoorten, kleuren, bijzondere bestanddelen) voor de beschrijving.',
  },
];

const WERKSOORT_BY_CODE = new Map(WERKSOORT.map((w) => [w.code, w]));

/** Werksoort-code → label. */
export function werksoortLabel(code: string | null | undefined): string {
  return (code && WERKSOORT_BY_CODE.get(code)?.label) || '';
}

// ─── NEN 6693 → TI export-helpers ───────────────────────────────────────────

/** Grondsoort/laagtype (interne NEN-code) → TI `LaSoilTypeCode`. Gesteente-codes = rotslaag. */
export function soilTypeToTi(code: string | null | undefined, gesteenteTiCode?: string | null): string | null {
  if (!code) return null;
  if (code === 'ROTS') return gesteenteTiCode ?? null; // ⚠ Rots zonder gesteente → niet exporteerbaar
  return GRONDSOORT_TI[code] ?? LAAGTYPE_SPECIAAL_TI[code] ?? GESTEENTE_TI[code] ?? null;
}

/** Bijmenging (interne NEN-code) → TI `LaAd{n}TypeCode`. */
export function admixtureToTi(code: string | null | undefined): string | null {
  return code ? BIJMENGING_TI[code] ?? null : null;
}

/**
 * Bijmenging-intensiteit → TI `LaAd{n}GradationCode`. `kleiig` (k) kent geen gradatie → `null`.
 */
export function intensityToTi(bijmengingCode: string | null | undefined, intensiteitLabel: string | null | undefined): string | null {
  if (bijmengingCode === 'k') return null; // kleiig — geen gradatie
  if (!intensiteitLabel) return null;
  return INTENSITEIT.get(intensiteitLabel) ?? null;
}

/** Kleur (interne NEN-code) → TI `LaColourMainCode` / `LaColourSubCode`. */
export function colourToTi(code: string | null | undefined): string | null {
  return code ? KLEUR_TI[code] ?? null : null;
}

/** Tint (interne NEN-prefix, '' = standaard) → TI `LaColourShadeCode`. */
export function shadeToTi(prefix: string | null | undefined): string | null {
  if (prefix == null) return null;
  return KLEUR_TINT_TI[prefix] ?? null;
}

/** Mediaan → TI `LaMedianCode`, grondsoort-afhankelijk (alleen Z/Gr). */
export function medianToTi(soilCode: string | null | undefined, medianClass: string | null | undefined): string | null {
  if (!soilCode || !medianClass) return null;
  return MEDIAAN_TI[soilCode]?.[medianClass] ?? null;
}

const GESTEENTE_CODE_SET = new Set(GESTEENTE.values());

/**
 * BZB-soort (interne NEN-afkorting) → TI `LaSc{n}TypeCode`.
 * Gesteenten (als BZB) mappen naar hun TI-BZB-code; kent TerraIndex het gesteente niet als BZB,
 * dan terugval op 'BS' (bewerkt steen) — bewuste keuze zodat de laag niet verloren gaat.
 */
export function bzbSoortToTi(code: string | null | undefined): string | null {
  if (!code) return null;
  if (code in BZB_SOORT_TI) return BZB_SOORT_TI[code];
  if (GESTEENTE_CODE_SET.has(code)) return GESTEENTE_BZB_TI[code] ?? 'BS';
  return null;
}

/** True als een gesteente-BZB alleen via de 'BS' (bewerkt steen)-terugval geëxporteerd wordt. */
export function isGesteenteBzbFallback(code: string | null | undefined): boolean {
  return !!code && GESTEENTE_CODE_SET.has(code) && !(code in GESTEENTE_BZB_TI);
}

/**
 * Asfalt-subtype (label) → specifieke TI `LaSc{n}TypeCode`. Terugval op 'AS' (generiek
 * asfalt) als de subtype leeg/onbekend is, zodat de laag altijd als asfalt-BZB in
 * TerraIndex verschijnt.
 */
export function asfaltTypeToTi(asphaltType: string | null | undefined): string {
  return (asphaltType && ASFALT_TYPE_TI[asphaltType]) || 'AS';
}

/**
 * Ballastfractie (label) → specifieke TI `LaSc{n}TypeCode`. Terugval op 'BL' (generiek ballast)
 * als de fractie leeg/onbekend is, zodat de laag altijd als ballast-BZB in TerraIndex verschijnt.
 */
export function ballastFractieToTi(ballastFraction: string | null | undefined): string {
  return (ballastFraction && BALLAST_FRACTIE_TI[ballastFraction]) || 'BL';
}

/** BZB-volumeaandeel (NEN-klasse) → TI `LaSc{n}GradationCode`. */
export function bzbVolumeToTi(volumeClass: string | null | undefined): string | null {
  return volumeClass ? BZB_VOLUME_TI[volumeClass] ?? null : null;
}

/** BZB-herkomst → TI `LaSc{n}OriginCode`. */
export function bzbHerkomstToTi(origin: string | null | undefined): string | null {
  return origin ? BZB_HERKOMST_TI[origin] ?? null : null;
}
